import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  BookOpen, Plus, X, Edit2, Trash2, Search, Filter, Download, Upload,
  FileText, ChevronDown, ChevronUp, Trophy, AlertTriangle, CheckCircle,
  Printer, TrendingUp, BarChart3, GraduationCap, Lock, Unlock, Eye, Save,
  RotateCcw, FileDown, Table2, UserCheck, Users, Calendar, Award,
  ArrowUpDown, CheckCircle2, XCircle, Loader2, FileSpreadsheet,
  School, BookMarked, Clock, LayoutList, ArrowLeft, Layers, Pencil, RefreshCw 
} from "lucide-react";
import { gradesApi, classesApi, subjectsApi, studentsApi, teachersApi, apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";


// ==================== API RESPONSE HELPER ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    if (Array.isArray(res.data)) return res.data;
    if (Array.isArray(res.items)) return res.items;
    if (Array.isArray(res.results)) return res.results;
  }
  return [];
};

const getErrorDetail = (err: any, fallback: string = ''): string => {
  const data = (err as any)?.response?.data as any;
  return data?.detail || data?.message || err?.message || fallback;
};

// CORRECT localStorage keys for your project
const AUTH_STORAGE_KEY = 'auth-storage'
const SELECTED_YEAR_ID_KEY = 'lybibal-selected-year-id'
const SELECTED_YEAR_LABEL_KEY = 'lybibal-selected-year-label'
const SELECTED_YEAR_EVENT = 'lybibal-year-change'

// Recuperation school_id depuis auth-storage
const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY)
    if (authRaw) {
      const parsed = JSON.parse(authRaw)
      const state = parsed?.state || parsed
      if (state?.user?.school_id) {
        return Number(state.user.school_id)
      }
    }
  } catch (e) {
    console.warn('Failed to get school_id from auth-storage:', e)
  }
  return null
}

// Recuperation academic_year_id depuis lybibal-selected-year-id
const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY)
    if (yearId) return Number(yearId)
  } catch (e) {
    console.warn('Failed to get year_id:', e)
  }
  return null
}

// ==================== TYPES ====================
interface Grade {
  id: number;
  student_id: number;
  student_name?: string;
  matricule?: string;
  subject_id: number;
  subject_name?: string;
  sequence_id: number;
  sequence_name?: string;
  value: number;
  grade_type: string;
  teacher_name?: string;
  comment?: string;
  appreciation_fr?: string;
  appreciation_en?: string;
  is_validated?: boolean;
}

interface GradeSequence {
  id: number;
  name: string;
  label?: string;
  code?: string;
  start_date: string;
  end_date: string;
  is_open: boolean;
  academic_year_id: number;
  evaluation_period_id?: number;
  max_score?: number;
}

interface EvaluationPeriod {
  id: number;
  code: string;
  label_fr: string;
  label_en: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  sequences?: GradeSequence[];
}

interface Subject {
  id: number;
  name: string;
  label_fr?: string;
  label_en?: string;
  code?: string;
  coefficient: number;
  section?: string;
  specialty_id?: number;
  is_core?: boolean;
  level_id?: number;
  section_id?: number;
}

interface Student {
  id: number;
  matricule: string;
  first_name: string;
  last_name: string;
  full_name?: string;
  class_id?: number;
  class_name?: string;
  gender?: string;
}

interface ClassOption {
  id: number;
  name: string;
  level_id?: number;
  level_name?: string;
  cycle_name?: string;
  student_count?: number;
  school_id?: number;
  specialty_id?: number;
}

interface SectionOption {
  id: number;
  name: string;
  code?: string;
}

interface CycleOption {
  id: number;
  name: string;
  section_id: number;
  code?: string;
}

interface LevelOption {
  id: number;
  name: string;
  cycle_id: number;
  code?: string;
}

interface TeacherAssignment {
  id: number;
  teacher_id: number;
  subject_id: number;
  class_id: number;
  hours_per_week?: number;
  is_principal_teacher?: boolean;
  subject_name?: string;
  class_name?: string;
}

interface Competence {
  id: number;
  subject_id: number;
  trimester: number;
  number: number;
  label_fr: string;
  label_en?: string;
  /** Description détaillée affichée sous le sélecteur (1er cycle) */
  description?: string;
  max_score?: number;
  coefficient?: number;
}

// ✅ Parser les matières du 1er cycle (ex: "ANGLAIS TRIMESTRE 1 COMPETENCE 1")
function parseFirstCycleSubject(name: string) {
  const match = name.match(/^(.*?)\s+TRIMESTRE\s+(\d+)\s+COMPETENCE\s+(\d+)$/i);
  if (match) {
    return {
      baseName: match[1].trim(),
      trimester: parseInt(match[2]),
      competenceNumber: parseInt(match[3]),
      isFirstCycle: true,
    };
  }
  return { baseName: name, trimester: 0, competenceNumber: 0, isFirstCycle: false };
}

interface GradeEntry {
  student_id: number;
  value: number | null;
  appreciation: string;
  comment: string;
}

// ==================== HELPER FUNCTIONS ====================

const getSubjectDisplayName = (subject: Subject, lang: 'fr' | 'en'): string => {
  if (lang === 'fr' && subject.label_fr) return subject.label_fr;
  if (lang === 'en' && subject.label_en) return subject.label_en;
  return subject.name || subject.label_fr || subject.label_en || `Matiere #${subject.id}`;
};

const getAppreciation = (value: number | null, lang: 'fr' | 'en' = 'fr'): string => {
  if (value === null || value === undefined || isNaN(value)) return '';
  if (lang === 'fr') {
    if (value >= 18) return 'Excellent';
    if (value >= 16) return 'Tres Bien';
    if (value >= 14) return 'Bien';
    if (value >= 12) return 'Assez Bien';
    if (value >= 10) return 'Passable';
    if (value >= 8) return 'Insuffisant';
    if (value >= 6) return 'Faible';
    return 'Tres Faible';
  } else {
    if (value >= 18) return 'Excellent';
    if (value >= 16) return 'Very Good';
    if (value >= 14) return 'Good';
    if (value >= 12) return 'Fairly Good';
    if (value >= 10) return 'Pass';
    if (value >= 8) return 'Insufficient';
    if (value >= 6) return 'Weak';
    return 'Very Weak';
  }
};

const _getAppreciationColor = (value: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) return 'text-gray-400 bg-gray-50';
  if (value >= 16) return 'text-emerald-700 bg-emerald-50';
  if (value >= 14) return 'text-blue-700 bg-blue-50';
  if (value >= 12) return 'text-cyan-700 bg-cyan-50';
  if (value >= 10) return 'text-gray-700 bg-gray-50';
  if (value >= 8) return 'text-orange-700 bg-orange-50';
  return 'text-red-700 bg-red-50';
};

const getAppreciationBadgeColor = (value: number | null): string => {
  if (value === null || value === undefined || isNaN(value)) return 'bg-gray-100 text-gray-500';
  if (value >= 16) return 'bg-emerald-100 text-emerald-700';
  if (value >= 14) return 'bg-blue-100 text-blue-700';
  if (value >= 12) return 'bg-cyan-100 text-cyan-700';
  if (value >= 10) return 'bg-gray-100 text-gray-700';
  if (value >= 8) return 'bg-orange-100 text-orange-700';
  return 'bg-red-100 text-red-700';
};

// ==================== MAIN COMPONENT ====================

export default function GradesPage() {
  const auth = useAuthStore();
  const { lang } = useLang();

  // school_id et academic_year_id recuperes automatiquement
  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId)
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId)
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null)

  const t = {
    fr: {
      title: 'Saisie des Notes',
      subtitle: 'Entree, modification et gestion des notes des eleves',
      selectClass: 'Selectionner une classe',
      selectSubject: 'Selectionner une matiere',
      selectPeriod: 'Selectionner une periode',
      selectSequence: 'Selectionner une sequence',
      selectSection: 'Selectionner une section',
      selectCycle: 'Selectionner un cycle',
      selectLevel: 'Selectionner un niveau',
      allClasses: 'Toutes les classes',
      allSubjects: 'Toutes les matieres',
      allPeriods: 'Toutes les periodes',
      allSequences: 'Toutes les sequences',
      allSections: 'Toutes les sections',
      allCycles: 'Tous les cycles',
      allLevels: 'Tous les niveaux',
      allStudents: 'Tous les eleves',
      searchStudent: 'Rechercher un eleve...',
      matricule: 'Matricule',
      student: 'Eleve',
      subject: 'Matiere',
      sequence: 'Sequence',
      grade: 'Note',
      appreciation: 'Appreciation',
      comment: 'Commentaire',
      actions: 'Actions',
      save: 'Enregistrer',
      cancel: 'Annuler',
      edit: 'Modifier',
      delete: 'Supprimer',
      validate: 'Valider',
      export: 'Exporter Excel',
      import: 'Importer Excel',
      downloadTemplate: 'Modele Excel',
      entryMode: 'Mode Saisie',
      exitEntryMode: 'Quitter le mode saisie',
      noData: 'Aucune donnee',
      noStudents: 'Aucun eleve trouve',
      noGrades: 'Aucune note enregistree',
      noClassesForLevel: 'Aucune classe trouvee pour ce niveau. Verifiez que les classes ont un level_id en base.',
      selectFilters: 'Selectionnez une sequence, classe et matiere pour voir les notes',
      loading: 'Chargement...',
      saveSuccess: 'Notes sauvegardees avec succes',
      saveError: 'Erreur lors de la sauvegarde',
      confirmDelete: 'Etes-vous sur de vouloir supprimer cette note ?',
      sequenceOpen: 'Ouverte',
      sequenceClosed: 'Fermee',
      newSequence: 'Nouvelle Sequence',
      sequenceName: 'Nom de la sequence',
      startDate: 'Date de debut',
      endDate: 'Date de fin',
      create: 'Creer',
      period: 'Periode',
      totalStudents: 'eleves',
      gradesEntered: 'notes saisies',
      remaining: 'restantes',
      avg: 'Moy',
      min: 'Min',
      max: 'Max',
      success: 'Succes',
      error: 'Erreur',
      importSuccess: 'Import reussi',
      importError: "Erreur lors de l'import",
      exportSuccess: 'Export reussi',
      fileRequired: 'Veuillez selectionner un fichier',
      invalidFile: 'Fichier invalide',
      dragDrop: 'Glissez-deposez un fichier Excel ici',
      orClick: 'ou cliquez pour selectionner',
      processing: 'Traitement en cours...',
      class: 'Classe',
      refresh: 'Actualiser',
      sequences: 'Sequences',
      showAllStudents: "Afficher tous les eleves de l'etablissement",
      studentCount: 'eleve(s)',
      modifiedGrades: 'notes modifiees',
      editGrade: 'Modifier la note',
      deleteGrade: 'Supprimer la note',
      saveGrade: 'Sauvegarder la note',
      cancelEdit: 'Annuler',
      autoAppreciation: 'Appreciation auto',
      outOf: 'sur',
      gradePlaceholder: '0-20',
      optionalComment: 'Commentaire optionnel...',
      sequencesManagement: 'Gestion des Sequences',
      existingSequences: 'Sequences existantes',
      addSequence: 'Ajouter une sequence',
      sequenceCreated: 'Sequence creee',
      sequenceUpdated: 'Sequence mise a jour',
      sequenceDeleted: 'Sequence supprimee',
      confirmDeleteSequence: 'Supprimer cette sequence ?',
      editSequence: 'Modifier',
      deleteSequence: 'Supprimer',
      close: 'Fermer',
      sequencesTitle: "Sequences d'evaluation",
      subjectsWithoutGrades: 'Matieres sans notes',
      notFilled: 'non remplies',
      complete: 'complet',
      allSubjectsHaveGrades: 'Toutes les matieres ont des notes',
      trimester1: 'Trimestre 1',
      trimester2: 'Trimestre 2',
      trimester3: 'Trimestre 3',
      devoir1: 'Devoir 1',
      composition1: 'Composition 1',
      devoir2: 'Devoir 2',
      composition2: 'Composition 2',
      noSequences: 'Aucune sequence creee',
      createFirstSequence: 'Creez votre premiere sequence',
      sequenceTable: 'Tableau des sequences',
      sequenceDetails: 'Details de la sequence',
      back: 'Retour',
      status: 'Statut',
      open: 'Ouvrir',
      noOpenSequences: 'Aucune sequence ouverte',
      openSequenceRequired: 'Veuillez ouvrir une sequence pour saisir des notes',
      periods: 'Periodes',
      periodName: 'Nom de la periode',
      newPeriod: 'Nouvelle Periode',
      periodCreated: 'Periode creee',
      periodDeleted: 'Periode supprimee',
      confirmDeletePeriod: 'Supprimer cette periode ?',
      code: 'Code',
      order: 'Ordre',
      managePeriods: 'Gerer les periodes',
      manageSequences: 'Gerer les sequences',
      yearChanged: 'Annee scolaire changee - rechargement des donnees',
      noYearSelected: 'Aucune annee scolaire selectionnee',
      section: 'Section',
      cycle: 'Cycle',
      level: 'Niveau',
    },
    en: {
      title: 'Grade Entry',
      subtitle: 'Entry, modification and management of student grades',
      selectClass: 'Select a class',
      selectSubject: 'Select a subject',
      selectPeriod: 'Select a period',
      selectSequence: 'Select a sequence',
      selectSection: 'Select a section',
      selectCycle: 'Select a cycle',
      selectLevel: 'Select a level',
      allClasses: 'All classes',
      allSubjects: 'All subjects',
      allPeriods: 'All periods',
      allSequences: 'All sequences',
      allSections: 'All sections',
      allCycles: 'All cycles',
      allLevels: 'All levels',
      allStudents: 'All students',
      searchStudent: 'Search for a student...',
      matricule: 'ID',
      student: 'Student',
      subject: 'Subject',
      sequence: 'Sequence',
      grade: 'Grade',
      appreciation: 'Appreciation',
      comment: 'Comment',
      actions: 'Actions',
      save: 'Save',
      cancel: 'Cancel',
      edit: 'Edit',
      delete: 'Delete',
      validate: 'Validate',
      export: 'Export Excel',
      import: 'Import Excel',
      downloadTemplate: 'Excel Template',
      entryMode: 'Entry Mode',
      exitEntryMode: 'Exit Entry Mode',
      noData: 'No data',
      noStudents: 'No students found',
      noGrades: 'No grades recorded',
      noClassesForLevel: 'No classes found for this level. Please verify classes have a level_id in database.',
      selectFilters: 'Select a sequence, class and subject to view grades',
      loading: 'Loading...',
      saveSuccess: 'Grades saved successfully',
      saveError: 'Error saving grades',
      confirmDelete: 'Are you sure you want to delete this grade?',
      sequenceOpen: 'Open',
      sequenceClosed: 'Closed',
      newSequence: 'New Sequence',
      sequenceName: 'Sequence name',
      startDate: 'Start date',
      endDate: 'End date',
      create: 'Create',
      period: 'Period',
      totalStudents: 'students',
      gradesEntered: 'grades entered',
      remaining: 'remaining',
      avg: 'Avg',
      min: 'Min',
      max: 'Max',
      success: 'Success',
      error: 'Error',
      importSuccess: 'Import successful',
      importError: 'Import error',
      exportSuccess: 'Export successful',
      fileRequired: 'Please select a file',
      invalidFile: 'Invalid file',
      dragDrop: 'Drag and drop an Excel file here',
      orClick: 'or click to select',
      processing: 'Processing...',
      class: 'Class',
      refresh: 'Refresh',
      sequences: 'Sequences',
      showAllStudents: 'Show all school students',
      studentCount: 'student(s)',
      modifiedGrades: 'grades modified',
      editGrade: 'Edit grade',
      deleteGrade: 'Delete grade',
      saveGrade: 'Save grade',
      cancelEdit: 'Cancel',
      autoAppreciation: 'Auto appreciation',
      outOf: 'out of',
      gradePlaceholder: '0-20',
      optionalComment: 'Optional comment...',
      sequencesManagement: 'Sequence Management',
      existingSequences: 'Existing sequences',
      addSequence: 'Add sequence',
      sequenceCreated: 'Sequence created',
      sequenceUpdated: 'Sequence updated',
      sequenceDeleted: 'Sequence deleted',
      confirmDeleteSequence: 'Delete this sequence?',
      editSequence: 'Edit',
      deleteSequence: 'Delete',
      close: 'Close',
      sequencesTitle: 'Evaluation Sequences',
      subjectsWithoutGrades: 'Subjects without grades',
      notFilled: 'not filled',
      complete: 'complete',
      allSubjectsHaveGrades: 'All subjects have grades',
      trimester2: 'Term 2',
      trimester3: 'Term 3',
      devoir1: 'Homework 1',
      composition1: 'Exam 1',
      devoir2: 'Homework 2',
      composition2: 'Exam 2',
      noSequences: 'No sequences created',
      createFirstSequence: 'Create your first sequence',
      sequenceTable: 'Sequence table',
      sequenceDetails: 'Sequence details',
      back: 'Back',
      status: 'Status',
      open: 'Open',
      noOpenSequences: 'No open sequence',
      openSequenceRequired: 'Please open a sequence to enter grades',
      periods: 'Periods',
      periodName: 'Period name',
      newPeriod: 'New Period',
      periodCreated: 'Period created',
      periodDeleted: 'Period deleted',
      confirmDeletePeriod: 'Delete this period?',
      code: 'Code',
      order: 'Order',
      managePeriods: 'Manage periods',
      manageSequences: 'Manage sequences',
      yearChanged: 'Academic year changed - reloading data',
      noYearSelected: 'No academic year selected',
      section: 'Section',
      cycle: 'Cycle',
      level: 'Level',
    }
  }[lang];

  // ==================== STATE ====================
  const [grades, setGrades] = useState<Grade[]>([]);
  const [sequences, setSequences] = useState<GradeSequence[]>([]);
  const [evaluationPeriods, setEvaluationPeriods] = useState<EvaluationPeriod[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [allSubjects, setAllSubjects] = useState<Subject[]>([]);
  const [classSubjects, setClassSubjects] = useState<Subject[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [teacherAssignments, setTeacherAssignments] = useState<TeacherAssignment[]>([]);
  const [allowedClassIds, setAllowedClassIds] = useState<number[]>([]);
  const [teacherSubjectIds, setTeacherSubjectIds] = useState<number[]>([]);
  const [assignmentsLoaded, setAssignmentsLoaded] = useState(false);
  const [assignmentsError, setAssignmentsError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [_apiErrors, setApiErrors] = useState<string[]>([]);

  // ✅ NOUVEAU: Filtres en cascade Section / Cycle / Niveau
  const [sections, setSections] = useState<SectionOption[]>([]);
  const [cycles, setCycles] = useState<CycleOption[]>([]);
  const [levels, setLevels] = useState<LevelOption[]>([]);
  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [loadingClasses, setLoadingClasses] = useState(false);

  // Filters
  const [selectedSequence, setSelectedSequence] = useState<string>("");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedSubject, setSelectedSubject] = useState<string>("");
  const [selectedCompetence, setSelectedCompetence] = useState<string>("");
  const [competencies, setCompetencies] = useState<Competence[]>([]);
  const [loadingCompetencies, setLoadingCompetencies] = useState(false);
  const [isFirstCycle, setIsFirstCycle] = useState(false);
  const [firstCycleRawCompetencies, setFirstCycleRawCompetencies] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [showAllStudents, setShowAllStudents] = useState(false);

  // Entry mode
  const [entryMode, setEntryMode] = useState(false);
  const [entryGrades, setEntryGrades] = useState<Record<number, GradeEntry>>({});
  const [editedGrades, setEditedGrades] = useState<Record<number, { value: number; appreciation: string; comment: string }>>({});

  const [refreshCounter, setRefreshCounter] = useState(0)

  // Refs to hold latest ids to avoid re-subscribing event listeners
  const schoolIdRef = React.useRef<number | null>(schoolId)
  const yearIdRef = React.useRef<number | null>(selectedAcademicYearId)

  const studentsSchoolIdRef = useRef(schoolId);
  useEffect(() => { studentsSchoolIdRef.current = schoolId; }, [schoolId]);

  useEffect(() => { schoolIdRef.current = schoolId }, [schoolId])
  useEffect(() => { yearIdRef.current = selectedAcademicYearId }, [selectedAcademicYearId])

  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {}
    if (schoolId) params.school_id = schoolId
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId
    return params
  }, [schoolId, selectedAcademicYearId])

  // Listen for year-change events
  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const customEvent = e as CustomEvent
      const detail = customEvent.detail
      if (!detail?.year_id) return

      const newYearId = Number(detail.year_id)
      const newSchoolId = detail?.school_id !== undefined ? Number(detail.school_id) : null

      if (newYearId && newYearId === yearIdRef.current && (newSchoolId === null || newSchoolId === schoolIdRef.current)) {
        return
      }

      if (newYearId && newYearId !== yearIdRef.current) {
        setSelectedAcademicYearId(newYearId)
        const stored = localStorage.getItem(SELECTED_YEAR_ID_KEY)
        if (stored !== String(detail.year_id)) {
          try { localStorage.setItem(SELECTED_YEAR_ID_KEY, detail.year_id.toString()) } catch (err) { /* ignore */ }
        }
        if (detail.label) {
          const storedLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
          try { if (storedLabel !== detail.label) localStorage.setItem(SELECTED_YEAR_LABEL_KEY, detail.label) } catch (err) { /* ignore */ }
          setCurrentAcademicYear({ id: newYearId, name: detail.label })
        }
      }

      if (newSchoolId !== null && !isNaN(newSchoolId) && newSchoolId !== schoolIdRef.current) {
        setSchoolId(newSchoolId)
        try { localStorage.setItem('lybibal-selected-school-id', newSchoolId.toString()); } catch (err) { /* ignore */ }
      }

      setSelectedSequence('')
      setSelectedClass('')
      setSelectedSubject('')
      setSelectedSection('')
      setSelectedCycle('')
      setSelectedLevel('')
      setGrades([])
      setEntryGrades({})
      setAssignmentsLoaded(false)
      setRefreshCounter(prev => prev + 1)

      toast.info(lang === 'fr' ? 'Annee scolaire changee - rechargement des donnees' : 'Academic year changed - reloading data')
    }

    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange)

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_YEAR_ID_KEY) {
        const newYearId = e.newValue ? Number(e.newValue) : null
        if (newYearId && newYearId !== yearIdRef.current) {
          setSelectedAcademicYearId(newYearId)
          setSelectedSequence("")
          setSelectedClass("")
          setSelectedSubject("")
          setSelectedSection("")
          setSelectedCycle("")
          setSelectedLevel("")
          setGrades([])
          setEntryGrades({})
        }
      }
    }
    window.addEventListener("storage", handleStorageChange)

    return () => {
      window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange)
      window.removeEventListener("storage", handleStorageChange)
    }
  }, [lang])

  useEffect(() => {
      const yearId = getSelectedYearId()
      const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY)
      if (yearId) {
        setSelectedAcademicYearId(yearId)
        if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel })
      }
      try {
        const storedSchool = localStorage.getItem('lybibal-selected-school-id')
        if (storedSchool) {
          const sid = Number(storedSchool)
          if (!isNaN(sid)) setSchoolId(sid)
        }
      } catch (err) {
        // ignore
      }
  }, [refreshCounter])

  // Modals
  const [showSequenceModal, setShowSequenceModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [_loadingSubjectsStatus, setLoadingSubjectsStatus] = useState(false);

  // Stats
  const [stats, setStats] = useState({ total: 0, entered: 0, remaining: 0, avg: 0, min: 0, max: 0 });

  // Tri des eleves
  const [sortBy, setSortBy] = useState<"name" | "grade">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Matieres sans notes
  const [subjectsStatus, setSubjectsStatus] = useState<Array<{
    subject_id: number;
    subject_label: string;
    coefficient: number;
    grade_count: number;
    student_count: number;
    has_grades: boolean;
  }>>([]);

  // ==================== ROLE-BASED ACCESS ====================
  const isAdminRole = auth.isAdmin || auth.isSuperAdmin || auth.roleName === "proviseur";
  const isCenseurRole = auth.roleName === "censeur" || auth.roleName === "responsable_pedagogique";
  const isTeacherRole = auth.roleName === "teacher" || auth.roleName === "enseignant";
  const canManageSequences = auth.isAdmin || auth.isSuperAdmin || auth.roleName === "proviseur" || isCenseurRole;

  const [resolvedTeacherId, setResolvedTeacherId] = useState<number | null>(null);

  const teacherId = useMemo(() => {
    if (resolvedTeacherId) return resolvedTeacherId;
    const user = auth.user as any;
    if (user?.teacher_id) return user.teacher_id;
    if (user?.teacher?.id) return user.teacher.id;
    // NE PAS utiliser user.id comme fallback - ce n'est pas un teacher_id valide
    // Attendre la résolution via /teachers?user_id={user.id}
    return null;
  }, [auth.user, isTeacherRole, resolvedTeacherId]);

  // Résoudre le vrai teacher_id depuis le backend si nécessaire
  useEffect(() => {
    if (!isTeacherRole) return;
    const user = auth.user as any;
    if (user?.teacher_id || user?.teacher?.id) return; // déjà résolu
    if (!user?.id) return;

    let cancelled = false;
    // Chercher le profil teacher lié à ce user (sans filtre school_id qui peut bloquer)
    apiService.get<any>('/teachers', { params: { user_id: user.id } })
      .then((res: any) => {
        if (cancelled) return;
        const data = Array.isArray(res) ? res : extractData(res);
        const teachers = Array.isArray(data) ? data : [];
        // Filtrer côté client par user_id au cas où l'API ne filtre pas
        // ✅ UNIQUEMENT sur user_id : un user_id n'est JAMAIS un teacher_id.
        // (Madame: user 111 ↔ prof 84 — l'ancien test `t.id === user.id`
        // récupérait un mauvais prof → "Aucune classe assignée".)
        const teacher = teachers.find((t: any) => Number(t.user_id) === Number(user.id));
        if (teacher?.id) {
          console.log(`[GradesPage] Resolved teacher_id: ${teacher.id} for user_id: ${user.id}`);
          setResolvedTeacherId(teacher.id);
        } else {
          console.warn(`[GradesPage] Aucun profil teacher lié au user_id ${user.id} — vérifiez teachers.user_id en base.`);
        }
      })
      .catch((err: any) => {
        console.warn('[GradesPage] Failed to resolve teacher_id:', err);
      });
    return () => { cancelled = true; };
  }, [isTeacherRole, auth.user]);

  // ==================== DATA LOADING ====================

  // ✅ NOUVEAU: Charger les sections
  const loadSections = useCallback(async () => {
    if (!schoolId) return;
    try {
      const filterParams = getFilterParams();
      const res = await apiService.get<any>("/sections", { params: filterParams });
      const data = Array.isArray(res) ? res : extractData(res);
      const mapped: SectionOption[] = Array.isArray(data) ? data.map((s: any) => ({
        id: s.id,
        name: s.name || s.label_fr || s.label_en || `Section #${s.id}`,
        code: s.code,
      })) : [];
      setSections(mapped);
    } catch (err) {
      console.error("Error loading sections:", err);
      setSections([]);
    }
  }, [schoolId, getFilterParams]);

  // ✅ NOUVEAU: Charger les cycles
  const loadCycles = useCallback(async () => {
    if (!schoolId || !selectedSection) {
      setCycles([]);
      return;
    }
    try {
      const filterParams = getFilterParams();
      const res = await apiService.get<any>("/cycles", { params: { ...filterParams, section_id: selectedSection } });
      const data = Array.isArray(res) ? res : extractData(res);
      const mapped: CycleOption[] = Array.isArray(data) ? data.map((c: any) => ({
        id: c.id,
        name: c.name || c.label_fr || c.label_en || `Cycle #${c.id}`,
        section_id: c.section_id,
        code: c.code,
      })) : [];
      setCycles(mapped);
    } catch (err) {
      console.error("Error loading cycles:", err);
      setCycles([]);
    }
  }, [schoolId, selectedSection, getFilterParams]);

  // ✅ NOUVEAU: Charger les niveaux
  const loadLevels = useCallback(async () => {
    if (!schoolId || !selectedCycle) {
      setLevels([]);
      return;
    }
    try {
      const filterParams = getFilterParams();
      const res = await apiService.get<any>("/levels", { params: { ...filterParams, cycle_id: selectedCycle } });
      const data = Array.isArray(res) ? res : extractData(res);
      const mapped: LevelOption[] = Array.isArray(data) ? data.map((l: any) => ({
        id: l.id,
        name: l.name || l.label_fr || l.label_en || `Level #${l.id}`,
        cycle_id: l.cycle_id,
        code: l.code,
      })) : [];
      setLevels(mapped);
    } catch (err) {
      console.error("Error loading levels:", err);
      setLevels([]);
    }
  }, [schoolId, selectedCycle, getFilterParams]);

  // ✅ CORRECTION: Charger les classes via /classes avec filtres section/cycle/niveau
    // ✅ CORRECTION: Charger les classes via /classes?level_id=... (comme StudentsPage)
  const loadAllowedClasses = useCallback(async () => {
    if (!selectedAcademicYearId) {
      setAllowedClassIds([]);
      setTeacherSubjectIds([]);
      setAssignmentsLoaded(true);
      return;
    }
    // Admin/proviseur: toutes les classes (pas de restriction)
    if (isAdminRole) {
      setAllowedClassIds([]);
      setTeacherSubjectIds([]);
      setAssignmentsLoaded(true);
      return;
    }
    // Enseignant: charger ses assignments depuis teacher_subject_classes
    if (isTeacherRole && teacherId) {
      setAssignmentsError(null);
      try {
        let assignments: any[] = [];
        let errorMsg: string | null = null;

        // ✅ V10 : sources multiples ET filtres progressifs. Certains
        // enseignants ont des affectations avec academic_year_id NULL ou une
        // autre année : le filtre année strict les écartait →
        // "Aucune classe assignée" alors que des classes existent.
        const trySources = async (params: Record<string, any>): Promise<any[]> => {
          // 1) /teachers/assignments
          try {
            const res = await apiService.get<any>('/teachers/assignments', { params });
            const data = Array.isArray(res) ? res : extractData(res);
            if (Array.isArray(data) && data.length > 0) return data;
          } catch (e) { /* source suivante */ }
          // 2) /timetable/assignments
          try {
            const res2 = await apiService.get<any>('/timetable/assignments', { params });
            const data2 = Array.isArray(res2) ? res2 : extractData(res2);
            if (Array.isArray(data2) && data2.length > 0) return data2;
          } catch (e) { /* source suivante */ }
          return [];
        };

        // Tentative 1 : avec l'année sélectionnée
        assignments = await trySources({ teacher_id: teacherId, academic_year_id: selectedAcademicYearId });
        console.log(`[GradesPage] Affectations (année ${selectedAcademicYearId}) pour teacher ${teacherId}: ${assignments.length}`);
        // Tentative 2 : SANS filtre année (affectations orphelines d'année)
        if (assignments.length === 0) {
          assignments = await trySources({ teacher_id: teacherId });
          console.log(`[GradesPage] Affectations (toutes années) pour teacher ${teacherId}: ${assignments.length}`);
          if (assignments.length > 0) errorMsg = null;
        }
        if (assignments.length === 0) {
          errorMsg = errorMsg || 'Aucune affectation trouvée pour cet enseignant';
        }

        if (errorMsg) {
          setAssignmentsError(errorMsg);
          toast.warning(lang === 'fr' 
            ? `Erreur chargement assignments: ${errorMsg}. Contactez l'admin.` 
            : `Assignment loading error: ${errorMsg}. Contact admin.`);
        }

        const normalizedAssignments = assignments.map((a: any) => ({
          id: a.id,
          teacher_id: a.teacher_id,
          subject_id: a.subject_id,
          class_id: a.class_id,
          hours_per_week: a.hours_per_week ?? 0,
          is_principal_teacher: a.is_principal_teacher ?? false,
          subject_name: a.subject_name,
          class_name: a.class_name,
        }));

        if (normalizedAssignments.length > 0) {
          setTeacherAssignments(normalizedAssignments);
        }
        const classIds = [...new Set(normalizedAssignments.map((a: any) => a.class_id).filter((id: any) => id !== undefined && id !== null))];
        const subjectIds = [...new Set(normalizedAssignments.map((a: any) => a.subject_id).filter((id: any) => id !== undefined && id !== null))];
        console.log(`[GradesPage] Teacher ${teacherId} allowed classes:`, classIds, 'subjects:', subjectIds);
        setAllowedClassIds(classIds);
        setTeacherSubjectIds(subjectIds);
      } catch (err) {
        console.error('[GradesPage] Error loading teacher assignments:', err);
        setAssignmentsError('Erreur inattendue');
      } finally {
        setAssignmentsLoaded(true);
      }
      return;
    }    // Censeur/surveillant: charger depuis /discipline/allowed-classes
    if (isCenseurRole) {
      try {
        const res = await apiService.get<any>('/discipline/allowed-classes');
        const data = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : extractData(res));
        const classIds = (Array.isArray(data) ? data : []).map((c: any) => c.id).filter((id: any) => id !== undefined);
        console.log(`[GradesPage] Censeur allowed classes:`, classIds);
        setAllowedClassIds(classIds);
        setTeacherSubjectIds([]);
      } catch (err) {
        console.error('[GradesPage] Error loading allowed classes:', err);
        setAllowedClassIds([]);
        setTeacherSubjectIds([]);
      } finally {
        setAssignmentsLoaded(true);
      }
      return;
    }
    // Autre rôle: pas de restriction
    setAllowedClassIds([]);
    setTeacherSubjectIds([]);
    setAssignmentsLoaded(true);
  }, [schoolId, selectedAcademicYearId, isAdminRole, isTeacherRole, isCenseurRole, teacherId]);

  const loadClassesFiltered = useCallback(async () => {
    if (!selectedAcademicYearId) {
      setClasses([]);
      return;
    }
    setLoadingClasses(true);
    try {
      const params: Record<string, any> = {};
      // NOTE: on ne filtre PAS par school_id ici car les classes dans la DB
      // peuvent avoir un school_id différent de celui de l'utilisateur connecté.
      // Ex: user.school_id=1 mais classes ont school_id=8. Le backend retournerait vide.
      // On filtre plutôt par academic_year_id et éventuellement level_id.
      if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
      // Passer level_id à l'API si le backend le supporte (filtrage côté serveur)
      if (selectedLevel) params.level_id = parseInt(selectedLevel);

      console.log('[GradesPage] Loading classes via /classes with params:', JSON.stringify(params));
      let res: any;
      if (classesApi.getAll) {
        res = await classesApi.getAll(params);
      } else {
        res = await apiService.get<any>("/classes", { params });
      }
      console.log('[GradesPage] /classes raw response:', res);

      const data = Array.isArray(res) ? res : extractData(res);
      console.log('[GradesPage] /classes extracted data:', data);

      let mapped: ClassOption[] = Array.isArray(data) ? data.map((c: any) => ({
        id: c.id,
        name: c.name || `Class #${c.id}`,
        level_id: c.level_id,
        level_name: c.level_name,
        cycle_name: c.cycle_name,
        student_count: c.student_count,
        specialty_id: c.specialty_id,
      })) : [];

      // Log de debug pour vérifier si level_id est présent
      const hasLevelId = mapped.some(c => c.level_id !== undefined && c.level_id !== null);
      console.log(`[GradesPage] Classes mapped: ${mapped.length}, has level_id: ${hasLevelId}`);
      if (mapped.length > 0) {
        console.log('[GradesPage] First class sample:', { id: mapped[0].id, name: mapped[0].name, level_id: mapped[0].level_id, school_id: mapped[0].school_id });
      }

      // Filtrer par classes autorisées pour enseignants/censeurs non-admin
      if (!isAdminRole && allowedClassIds.length > 0) {
        const beforeFilter = mapped.length;
        mapped = mapped.filter((c: ClassOption) => allowedClassIds.includes(c.id));
        console.log(`[GradesPage] Classes filtered by permissions: ${beforeFilter} -> ${mapped.length}`);
      }
      setClasses(mapped);
    } catch (err) {
      console.error("Error loading classes:", err);
      setClasses([]);
    } finally {
      setLoadingClasses(false);
    }
  }, [schoolId, selectedAcademicYearId, selectedLevel, allowedClassIds, isAdminRole]);

  const loadInitialData = useCallback(async () => {
    if (!selectedAcademicYearId) {
      console.warn("selectedAcademicYearId not available, skipping loadInitialData")
      return
    }

    setLoading(true);
    setApiErrors([]);
    try {
      const filterParams = getFilterParams();

      let periodsData: any[] = [];
      try {
        console.debug('[GradesPage] loadInitialData - fetching periods with', filterParams)
        const res = await apiService.get<any>("/grades/periods", { params: filterParams });
        periodsData = Array.isArray(res) ? res : (extractData(res));
        console.debug('[GradesPage] periods response:', periodsData)
      } catch (err) { periodsData = []; console.warn('[GradesPage] periods fetch failed', err) }

      const periods: EvaluationPeriod[] = Array.isArray(periodsData) ? periodsData.map((p: any) => ({
        id: p.id, code: p.code || "T1", label_fr: p.label_fr || `Trimestre ${p.id}`,
        label_en: p.label_en || `Term ${p.id}`, start_date: p.start_date || "",
        end_date: p.end_date || "", is_active: p.is_active !== false, sequences: []
      })) : [];

      let seqsData: any[] = [];
      try {
        console.debug('[GradesPage] loadInitialData - fetching sequences with', filterParams)
        const res = await apiService.get<any>("/grades/sequences", { params: filterParams });
        seqsData = Array.isArray(res) ? res : (extractData(res));
        console.debug('[GradesPage] sequences raw response:', seqsData)

        if (Array.isArray(seqsData) && selectedAcademicYearId) {
          seqsData = seqsData.filter((s: any) => {
            const matchesYear = (s.academic_year_id === undefined || s.academic_year_id === null) ? true : Number(s.academic_year_id) === Number(selectedAcademicYearId);
            const matchesSchool = (s.school_id === undefined || s.school_id === null || !schoolId) ? true : Number(s.school_id) === Number(schoolId);
            return matchesYear && matchesSchool;
          });
          console.debug('[GradesPage] sequences filtered:', seqsData)
        }
      } catch { seqsData = []; }

      const seqs: GradeSequence[] = Array.isArray(seqsData) ? seqsData.map((s: any) => ({
        id: s.id, name: s.label || s.name || `Sequence ${s.id}`, label: s.label,
        code: s.code, start_date: s.start_date, end_date: s.end_date,
        is_open: s.is_active !== false, academic_year_id: s.academic_year_id || selectedAcademicYearId,
        evaluation_period_id: s.evaluation_period_id, max_score: s.max_score || 20
      })) : [];

      periods.forEach(p => { p.sequences = seqs.filter(s => s.evaluation_period_id === p.id); });
      setEvaluationPeriods(periods);
      setSequences(seqs);

      // ✅ Charger les sections
      await loadSections();

      let allSubjectsData: any[] = [];
      try {
        let res: any;
        if (teachersApi.getSubjects) { res = await teachersApi.getSubjects(filterParams); }
        else { res = await apiService.get<any>("/teachers/subjects", { params: filterParams }); }
        allSubjectsData = Array.isArray(res) ? res : (extractData(res));
      } catch { allSubjectsData = []; }

      const mappedSubjects: Subject[] = Array.isArray(allSubjectsData) ? allSubjectsData.map((s: any) => ({
        id: s.id, name: s.label_fr || s.label_en || s.name || `Matiere #${s.id}`,
        label_fr: s.label_fr, label_en: s.label_en, code: s.code,
        coefficient: s.coefficient !== undefined && s.coefficient !== null ? Number(s.coefficient) : 1,
        section: s.section,
        specialty_id: s.specialty_id, is_core: s.is_core,
      })) : [];
      setAllSubjects(mappedSubjects);

      if (isTeacherRole && teacherId) {
        let assignData: any[] = [];
        // V10 : repli sans filtre année (affectations orphelines d'année)
        const tryAssign = async (params: Record<string, any>): Promise<any[]> => {
          try {
            let res: any;
            if (teachersApi.getAssignments) {
              res = await teachersApi.getAssignments(params);
            } else {
              res = await apiService.get<any>("/teachers/assignments", { params });
            }
            const d = Array.isArray(res) ? res : extractData(res);
            return Array.isArray(d) ? d : [];
          } catch { return []; }
        };
        const assignmentParams: Record<string, any> = { teacher_id: teacherId };
        if (schoolId) assignmentParams.school_id = schoolId;
        if (selectedAcademicYearId) assignmentParams.academic_year_id = selectedAcademicYearId;
        assignData = await tryAssign(assignmentParams);
        if (assignData.length === 0) {
          const { academic_year_id, ...noYear } = assignmentParams;
          assignData = await tryAssign(noYear);
        }
        const assignments = Array.isArray(assignData) ? assignData : [];
        setTeacherAssignments(assignments);
        const classIds = [...new Set(assignments.map((a: any) => a.class_id).filter((id: any) => id !== undefined && id !== null))];
        const subjectIds = [...new Set(assignments.map((a: any) => a.subject_id).filter((id: any) => id !== undefined && id !== null))];
        setAllowedClassIds(classIds);
        setTeacherSubjectIds(subjectIds);
        setAssignmentsLoaded(true);
        console.log(`[GradesPage] loadInitialData: teacher ${teacherId} has ${assignments.length} assignments, classes:`, classIds, 'subjects:', subjectIds);
      }

      // Charger les classes autorisées pour censeurs (enseignants déjà traités ci-dessus)
      if (!isTeacherRole) {
        await loadAllowedClasses();
      }
      // Charger les classes pour tout le monde (même si schoolId est null)
      await loadClassesFiltered();

      if (isAdminRole || isCenseurRole) {
        // V8 FIX : pagination complète (sans per_page → 20 max avant)
        const allStudentsData: any[] = [];
        const seenStu = new Set<number>();
        let stuPage = 1;
        let stuTotal = 0;
        while (stuPage <= 60) {
          try {
            const stuParams: Record<string, any> = { ...filterParams, active_only: true, page: stuPage, per_page: 200 };
            let res: any;
            if (studentsApi.getAll) { res = await studentsApi.getAll(stuParams); }
            else { res = await apiService.get<any>("/students", { params: stuParams }); }
            const arr = Array.isArray(res) ? res : extractData(res);
            stuTotal = (res && typeof res.total === 'number') ? res.total : arr.length;
            arr.forEach((stu: any) => {
              const id = stu?.id ?? stu?.student?.id;
              if (id !== undefined && id !== null && !seenStu.has(Number(id))) { seenStu.add(Number(id)); allStudentsData.push(stu); }
            });
            if (arr.length === 0 || allStudentsData.length >= stuTotal) break;
            stuPage++;
          } catch { break; }
        }
        setAllStudents(allStudentsData);
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      toast.error(t.error);
    } finally { setLoading(false); }
  }, [isTeacherRole, isAdminRole, isCenseurRole, teacherId, lang, schoolId, selectedAcademicYearId, getFilterParams, loadSections, loadAllowedClasses]);

  // ✅ CORRECTION: Charger les matières de LA CLASSE avec LES COEFFICIENTS DE LA CLASSE
  const loadClassSubjects = useCallback(async () => {
    if (!selectedClass) {
      setClassSubjects([]);
      setFirstCycleRawCompetencies([]);
      setIsFirstCycle(false);
      return;
    }
    const classId = parseInt(selectedClass);
    const selectedClassObj = classes.find(c => c.id === classId);
    const classLevelId = selectedClassObj?.level_id;
    let allData: any[] = [];
    let competenciesData: any[] = [];

    // ═══════════════════════════════════════════════════════════════
    // ÉTAPE 1 : Compétences du 1er cycle (inchangé)
    // ═══════════════════════════════════════════════════════════════
    if (classLevelId) {
      try {
        console.log(`[GradesPage] Trying /competencies?level_id=${classLevelId}`);
        const res = await apiService.getCompetencies({ level_id: classLevelId, school_id: schoolId, academic_year_id: selectedAcademicYearId });
        const data = Array.isArray(res) ? res : extractData(res);
        if (Array.isArray(data) && data.length > 0) {
          console.log(`[GradesPage] ✓ /competencies returned ${data.length} competencies — 1ER CYCLE DETECTED`);
          competenciesData = data;
          setIsFirstCycle(true);
          setFirstCycleRawCompetencies(data);
        } else {
          console.log(`[GradesPage] ✗ /competencies returned empty — not 1er cycle`);
          setIsFirstCycle(false);
          setFirstCycleRawCompetencies([]);
        }
      } catch (err: any) {
        console.log(`[GradesPage] ✗ /competencies failed:`, err.message || err);
        setIsFirstCycle(false);
        setFirstCycleRawCompetencies([]);
      }
    }

    // ═══════════════════════════════════════════════════════════════
    // ÉTAPE 2 : Matières de la classe avec coefficients OFFICIELS
    // ✅ CORRECTION : chargée aussi en 1er cycle (avant : sautée → coef 1 partout)
    // ═══════════════════════════════════════════════════════════════
    {

      // 2a. ✅ PRIORITÉ : /grades/subjects?class_id= (coefficients OFFICIELS depuis subject_specialties)
      // NOTE: /classes/{id}/subjects renvoie 404 → appel supprimé, /grades/subjects couvre ce besoin.
      if (allData.length === 0) {
        try {
          const params: Record<string, any> = { class_id: classId };
          if (classLevelId) params.level_id = classLevelId;
          if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
          if (isTeacherRole && teacherId) params.teacher_id = teacherId;
          console.log(`[GradesPage] Trying /grades/subjects (coefficients officiels) with params=`, params);
          const res = await apiService.getGradeSubjects(params);
          const data = Array.isArray(res) ? res : extractData(res);
          if (Array.isArray(data) && data.length > 0) {
            console.log(`[GradesPage] ✓ /grades/subjects returned ${data.length} subjects`);
            allData = data;
          }
        } catch (err: any) {
          console.log(`[GradesPage] ✗ /grades/subjects failed:`, err.message || err);
        }
      }

      // 2b. Fallback /subject-classes?class_id= (lien matière-classe avec coefficient)
      if (allData.length === 0) {
        try {
          console.log(`[GradesPage] Trying /subject-classes?class_id=${classId}`);
          const res = await apiService.get<any>("/subject-classes", {
            params: { class_id: classId, academic_year_id: selectedAcademicYearId },
          });
          const data = Array.isArray(res) ? res : extractData(res);
          if (Array.isArray(data) && data.length > 0) {
            console.log(`[GradesPage] ✓ /subject-classes returned ${data.length} rows`);
            allData = data.map((sc: any) =>
              sc.subject
                ? { ...sc.subject, class_coefficient: sc.coefficient ?? sc.class_coefficient ?? sc.subject?.coefficient }
                : sc
            );
          }
        } catch (err: any) {
          console.log(`[GradesPage] ✗ /subject-classes failed:`, err.message || err);
        }
      }

      // 2c. Fallback /subjects?level_id=
      if (allData.length === 0 && classLevelId) {
        try {
          const params = { level_id: classLevelId, school_id: schoolId, academic_year_id: selectedAcademicYearId };
          console.log(`[GradesPage] Trying /subjects with params=`, params);
          const res = await apiService.getSubjects(params);
          const data = Array.isArray(res) ? res : extractData(res);
          if (Array.isArray(data) && data.length > 0) {
            console.log(`[GradesPage] ✓ /subjects returned ${data.length} subjects`);
            allData = data;
          }
        } catch (err: any) {
          console.log(`[GradesPage] ✗ /subjects failed:`, err.message || err);
        }
      }

      // 2e. Fallback /timetable/class-subjects/{id}
      if (allData.length === 0) {
        try {
          console.log(`[GradesPage] Trying /timetable/class-subjects/${classId}`);
          const res = await apiService.getClassSubjects(classId);
          const data = Array.isArray(res) ? res : extractData(res);
          if (Array.isArray(data) && data.length > 0) {
            console.log(`[GradesPage] ✓ /timetable/class-subjects/${classId} returned ${data.length} subjects`);
            allData = data;
          }
        } catch (err: any) {
          console.log(`[GradesPage] ✗ /timetable/class-subjects/${classId} failed:`, err.message || err);
        }
      }

      // 2f. Si enseignant, fallback depuis ses assignments
      if (allData.length === 0 && isTeacherRole && teacherAssignments.length > 0) {
        const assignedSubjectIds = teacherAssignments
          .filter(a => a.class_id === classId)
          .map(a => a.subject_id);
        if (assignedSubjectIds.length > 0 && allSubjects.length > 0) {
          allData = allSubjects.filter(s => assignedSubjectIds.includes(s.id));
          console.log(`[GradesPage] Fallback from teacher assignments: ${allData.length} subjects`);
        }
      }
    }


    // ═══════════════════════════════════════════════════════════════
    // ÉTAPE 2-NEW : /subject-specialties?level_id= — matières du niveau.
    // On garde : spécialité "Générale" + spécialité de LA CLASSE.
    // Détection de la spécialité de la classe (dans l'ordre) :
    //   1. specialty_id de la liste /classes
    //   2. GET /classes/{id} (détail — renvoie toujours specialty_id)
    //   3. correspondance du nom de classe ("4e Allemand" → "allemand")
    //   4. spécialité non-générale unique du niveau (6e, 5e, Form 1-4)
    // ═══════════════════════════════════════════════════════════════
    if (allData.length === 0 && classLevelId) {
      try {
        console.log(`[GradesPage] Trying /subject-specialties?level_id=${classLevelId}`);
        const res = await apiService.get<any>("/subject-specialties", {
          params: { level_id: classLevelId },
        });
        const data = Array.isArray(res) ? res : extractData(res);
        if (Array.isArray(data) && data.length > 0) {
          // ── Diagnostic : liste des spécialités du niveau ──
          const specMap = new Map<number, string>();
          data.forEach((r: any) => {
            if (!specMap.has(r.specialty_id)) specMap.set(r.specialty_id, `${r.specialty_name}(${r.specialty_code})#${r.specialty_id}`);
          });
          console.log('[GradesPage] Specialties in level:', Array.from(specMap.values()).join(', '));

          // ── 1/2. specialty_id de la classe (liste, sinon détail) ──
          let cls: any = classes.find(c => c.id === classId);
          let classSpecId: number | undefined = cls?.specialty_id;
          if (!classSpecId) {
            try {
              const detail: any = await apiService.get<any>(`/classes/${classId}`);
              classSpecId = detail?.specialty_id ?? detail?.data?.specialty_id;
              if (classSpecId) console.log(`[GradesPage] Class specialty_id via /classes/${classId}:`, classSpecId);
            } catch (e) { /* détail indisponible : on continue */ }
          }

          const norm = (v: any) => String(v || '')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase().trim();
          const clsName = norm(cls?.name || '');
          const SPEC_STOP_WORDS = new Set(['serie', 'series', 'et', 'de', 'des', 'du', 'la', 'le', 'les', 'en', 'au', 'aux', 'a', 'avec', 'option', 'filiere']);
          const specTokens = (v: any) => norm(v).split(/\s+/).filter((w: string) => w.length > 1 && !SPEC_STOP_WORDS.has(w));
          const specSingleTokens = (v: any) => norm(v).split(/\s+/).filter((w: string) => w.length === 1 && !SPEC_STOP_WORDS.has(w));
          const isGeneralSpec = (row: any) => {
            const n = norm(row.specialty_name || row.specialty_abbr);
            const c = norm(row.specialty_code);
            return n.includes('general') || n.includes('commun') || n.includes('tronc')
              || /^gen/.test(c) || /^tc/.test(c);
          };
          // V-FIX : matching nom de classe <-> specialite par TOKENS (robuste).
          // Ancien matching (1er mot du nom + code ENTier) echouait pour
          // code "Serie A4" vs classe "Terminale A4". Gere aussi les series
          // a une lettre ("Serie C" <-> "Terminale C") et l'abreviation
          // specialty_abbr renvoyee par l'API.
          const matchesClassByName = (row: any): boolean => {
            if (!clsName) return false;
            const clsTokens = clsName.split(/\s+/).filter((w: string) => w.length > 0);
            const codeTokens = specTokens(row.specialty_code);
            const nameTokens = specTokens(row.specialty_name);
            const abbrTokens = specTokens(row.specialty_abbr);
            const singleTokens = [
              ...specSingleTokens(row.specialty_code),
              ...specSingleTokens(row.specialty_name),
              ...specSingleTokens(row.specialty_abbr),
            ];
            // 1) Token (>= 2 car.) du code / nom / abreviation contenu dans le nom de classe
            if ([...codeTokens, ...nameTokens, ...abbrTokens].some((w: string) => clsName.includes(w))) return true;
            // 2) Token du nom de classe = token specialite (incl. series 1 lettre : C, D…)
            if (clsTokens.some((ct: string) =>
              codeTokens.includes(ct) || nameTokens.includes(ct) || abbrTokens.includes(ct) || singleTokens.includes(ct)
            )) return true;
            return false;
          };
          const nonGeneralSpecs = data
            .map((r: any) => r.specialty_id)
            .filter((id: any, idx: number, arr: number[]) => !isGeneralSpec(data[idx]) && arr.indexOf(id) === idx);
          const singleNonGeneral = nonGeneralSpecs.length === 1 ? nonGeneralSpecs[0] : null;

          // ⚠️ singleNonGeneral ne s'applique QUE si la spécialité de la classe
          // est inconnue (sinon une classe Espagnole hériterait de l'Allemand)
          const generalRows = data.filter((r: any) => isGeneralSpec(r));
          let classRows: any[] = [];
          if (classSpecId) {
            classRows = data.filter((r: any) => r.specialty_id === classSpecId);
            if (classRows.length === 0) {
              // V-FIX : specialty_id en base incoherent (aucune ligne) -> repli
              // par matching nom de classe pour ne pas perdre les matieres.
              const byName = data.filter((r: any) => !isGeneralSpec(r) && matchesClassByName(r));
              if (byName.length > 0) {
                console.warn(`[GradesPage] specialty_id=${classSpecId} sans ligne correspondante - repli matching nom de classe (${byName.length} lignes)`);
                classRows = byName;
              }
            }
          } else {
            classRows = data.filter((r: any) =>
              matchesClassByName(r) || (singleNonGeneral !== null && r.specialty_id === singleNonGeneral)
            );
          }
          // Fusion : quand une matière existe dans « Générale » ET dans la
          // spécialité de la classe, c'est la ligne de LA CLASSE qui gagne
          // (coef spécifique : Maths coef 4 en série C vs coef 2 général —
          // avant, generalRows en premier écrasait le bon coefficient).
          const rowBySubject = new Map<number, any>();
          generalRows.forEach((r: any) => rowBySubject.set(r.subject_id, r));
          classRows.forEach((r: any) => rowBySubject.set(r.subject_id, r));
          const rows = Array.from(rowBySubject.values());
          console.log(`[GradesPage] ✓ /subject-specialties: ${data.length} rows, ${rows.length} kept (class="${cls?.name}" spec_id=${classSpecId}, singleNonGeneral=${singleNonGeneral})`);
          if (rows.length === 0) {
            console.warn(`[GradesPage] ⚠️ 0 ligne conservee sur ${data.length} - specialites du niveau:`, Array.from(specMap.values()), `- classe="${cls?.name}" spec_id=${classSpecId}`);
          }
          if (rows.length > 0) {
            allData = rows.map((r: any) => ({
              id: r.subject_id,
              label_fr: r.subject_label_fr,
              label_en: r.subject_label_en,
              code: r.subject_code,
              coefficient: r.coefficient ?? 1,
              specialty_id: r.specialty_id,
              level_id: classLevelId,
            }));
          }
        }
      } catch (err: any) {
        console.log(`[GradesPage] ✗ /subject-specialties failed:`, err.message || err);
      }
    }

    // ✅ Mapper — coefficient = COEFFICIENT DE LA CLASSE en priorité (pas le coef global)
    const mapped: Subject[] = allData.map((s: any) => ({
      id: s.id,
      name: s.label_fr || s.label_en || s.name || s.subject_name || `Matiere #${s.id}`,
      label_fr: s.label_fr,
      label_en: s.label_en,
      code: s.code,
      coefficient: Number(s.class_coefficient ?? s.classCoefficient ?? s.coefficient ?? s.coef ?? 1),
      section: s.section ?? s.section_name ?? s.section_code,
      section_id: s.section_id,
      specialty_id: s.specialty_id,
      is_core: s.is_core,
      level_id: s.level_id,
    }));

    // ✅ Filtrer par section sélectionnée (filtre doux : on garde si pas d'info section)
    // ✅ CORRECTION : l'API renvoie section: "FR"/"EN" (code), pas le nom de la section.
    // On normalise les deux côtés (FR ↔ francophone, EN ↔ anglophone) et on ne laisse
    // JAMAIS ce filtre doux vider la liste — sinon fallback sur allSubjects (coef 1 partout).
    const SECTION_EQUIV: Record<string, string[]> = {
      fr: ['fr', 'francophone', 'french'],
      en: ['en', 'anglophone', 'english', 'anglais'],
    }
    const normalizeSection = (v: string) => (v || '').toLowerCase().trim()
    const sectionMatches = (subjectSection: string, selectedName: string): boolean => {
      const s = normalizeSection(subjectSection)
      const n = normalizeSection(selectedName)
      if (!s || !n) return true
      for (const [key, equivalents] of Object.entries(SECTION_EQUIV)) {
        if (equivalents.includes(s) && equivalents.includes(n)) return true
      }
      return s === n
    }
    const selectedSectionName = sections.find(x => String(x.id) === selectedSection)?.name || '';
    let sectionFiltered = selectedSection
      ? mapped.filter((s: any) => {
          if (s.section_id !== undefined && s.section_id !== null) return String(s.section_id) === selectedSection;
          if (s.section && selectedSectionName) return sectionMatches(String(s.section), selectedSectionName);
          return true;
        })
      : mapped;
    if (selectedSection && sectionFiltered.length === 0 && mapped.length > 0) {
      console.warn(`[GradesPage] Filtre section "${selectedSectionName}" a tout écarté — conservation de la liste complète (${mapped.length} matières)`);
      sectionFiltered = mapped;
    }

    console.log(`[GradesPage] classSubjects final for class ${classId}:`, sectionFiltered.length, sectionFiltered.map(s => `${s.name} (coef. ${s.coefficient})`));
    setClassSubjects(sectionFiltered);
  }, [selectedClass, classes, allSubjects, teacherAssignments, isTeacherRole, teacherId, schoolId, selectedAcademicYearId, selectedSection, sections]);

  const _loadStudents = useCallback(async () => {
    if (!selectedClass) {
      if (showAllStudents) { setStudents(allStudents); }
      else { setStudents([]); }
      return;
    }
    // Déléguer à la fonction robuste
    await loadStudentsForClass(parseInt(selectedClass));
  }, [selectedClass, showAllStudents, allStudents]);

  // Fonction ROBUSTE pour charger les élèves d'une classe (indépendante du state selectedClass)
  // V8 FIX : pagination complète — l'appel sans per_page renvoyait 20 élèves
  // (défaut backend) quelle que soit la taille réelle de la classe. On pagine
  // (200/page) avec active_only=true pour n'obtenir QUE les élèves inscrits
  // de l'année (identique aux listes provisoire/définitive).
  const loadStudentsForClass = useCallback(async (classId: number) => {
    try {
      const all: any[] = [];
      const seen = new Set<number>();
      let apiTotal = 0;
      let page = 1;
      while (page <= 60) { // 60 pages × 200 = 12 000 élèves max
        const params: Record<string, any> = {
          class_id: classId,
          active_only: true,
          page,
          per_page: 200,
        };
        if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
        if (studentsSchoolIdRef.current) params.school_id = studentsSchoolIdRef.current;

        let res: any;
        if (studentsApi.getAll) {
          res = await studentsApi.getAll(params);
        } else {
          res = await apiService.get<any>("/students", { params });
        }
        const arr = Array.isArray(res) ? res : extractData(res);
        apiTotal = (res && typeof res.total === 'number') ? res.total : arr.length;
        arr.forEach((stu: any) => {
          const id = stu?.id ?? stu?.student?.id;
          if (id !== undefined && id !== null && !seen.has(Number(id))) {
            seen.add(Number(id));
            all.push(stu);
          }
        });
        if (arr.length === 0 || all.length >= apiTotal) break;
        page++;
      }
      console.log(`[GradesPage] loadStudentsForClass(${classId}): ${all.length} élève(s) inscrit(s) chargé(s) (total API: ${apiTotal})`);
      setStudents(all);
    } catch (err) {
      console.error("Error loading students:", err);
      setStudents([]);
    }
  }, [selectedAcademicYearId]); // ref pour school_id, year en dépendance

  const loadSubjectsStatus = useCallback(async () => {
    if (!selectedClass || !selectedSequence) {
      setSubjectsStatus([]);
      return;
    }
    if (!isAdminRole && !isCenseurRole && auth.roleName !== "proviseur") {
      return;
    }
    setLoadingSubjectsStatus(true);
    try {
      const params: Record<string, any> = {
        class_id: parseInt(selectedClass),
        sequence_id: parseInt(selectedSequence),
      };
      if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
      const res = await apiService.get("/grades/subjects-status", { params });
      const data = Array.isArray(res) ? res : (extractData(res));
      setSubjectsStatus(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading subjects status:", err);
    } finally {
      setLoadingSubjectsStatus(false);
    }
  }, [selectedClass, selectedSequence, isAdminRole, isCenseurRole, auth.roleName, getFilterParams]);



  const loadGrades = useCallback(async () => {
    if (!selectedClass || !selectedSubject || !selectedSequence) {
      setGrades([]);
      return;
    }
    // 1er cycle : une compétence doit être sélectionnée
    if (isFirstCycle && !selectedCompetence) {
      setGrades([]);
      return;
    }
    setLoading(true);
    try {
      const params: any = {
        class_id: parseInt(selectedClass),
        sequence_id: parseInt(selectedSequence),
      };
      if (isFirstCycle && selectedCompetence) {
        // 1er cycle : charger par competence_id
        params.competence_id = parseInt(selectedCompetence);
      } else {
        // 2nd cycle : charger par subject_id
        params.subject_id = parseInt(selectedSubject);
      }
      // ✅ Ne PAS passer school_id — les classes ont school_id=8, l'user a school_id=1
      if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;

      let data: any[] = [];
      const fetchGrades = async (p: any): Promise<any[]> => {
        if (gradesApi.getGrades) {
          const res = await gradesApi.getGrades(p);
          return Array.isArray(res) ? res : extractData(res);
        }
        const res = await apiService.get<any>("/grades", { params: p });
        return Array.isArray(res) ? res : extractData(res);
      };
      data = await fetchGrades(params);
      // ✅ Repli année : les notes enregistrées avec une autre année (ou NULL)
      // ne remontaient pas quand l'année sélectionnée ne correspondait pas —
      // symptôme « notes déjà remplies invisibles ». On retente sans filtre.
      if ((!data || data.length === 0) && selectedAcademicYearId) {
        const { academic_year_id, ...paramsNoYear } = params;
        const retry = await fetchGrades(paramsNoYear).catch(() => []);
        if (retry && retry.length > 0) data = retry;
      }
      setGrades(Array.isArray(data) ? data : []);

      if (Array.isArray(data) && data.length > 0) {
        const values = data.map((g: any) => g.value || g.score || 0).filter((v: number) => !isNaN(v));
        setStats({
          total: students.length, entered: data.length,
          remaining: Math.max(0, students.length - data.length),
          avg: values.length > 0 ? values.reduce((a: number, b: number) => a + b, 0) / values.length : 0,
          min: values.length > 0 ? Math.min(...values) : 0,
          max: values.length > 0 ? Math.max(...values) : 0,
        });
      } else {
        setStats({ total: students.length, entered: 0, remaining: students.length, avg: 0, min: 0, max: 0 });
      }
    } catch (err: any) {
      console.error("Error loading grades:", err);
      const status = err.response?.status;
      const detail = getErrorDetail(err);
      if (status === 500) {
        toast.error(lang === "fr" 
          ? `Erreur serveur (500). Detail: ${detail || "GradeResponse schema mismatch - contactez l'admin"}` 
          : `Server error (500). Detail: ${detail || "GradeResponse schema mismatch"}`);
      }
      setGrades([]);
    } finally { setLoading(false); }
  }, [selectedClass, selectedSubject, selectedSequence, selectedCompetence, isFirstCycle, students.length, lang, getFilterParams]);

  // Effet pour recharger les donnees quand schoolId ou selectedAcademicYearId changent
  useEffect(() => {
    if (selectedAcademicYearId) {
      loadInitialData()
    }
  }, [schoolId, selectedAcademicYearId, refreshCounter, loadInitialData, teacherId, isTeacherRole])

  // ✅ NOUVEAU: Charger les cycles quand la section change
  useEffect(() => {
    if (schoolId && selectedAcademicYearId) {
      loadCycles();
    }
  }, [loadCycles, schoolId, selectedAcademicYearId, selectedSection]);

  // ✅ NOUVEAU: Charger les niveaux quand le cycle change
  useEffect(() => {
    if (schoolId && selectedAcademicYearId) {
      loadLevels();
    }
  }, [loadLevels, schoolId, selectedAcademicYearId, selectedCycle]);

  // ✅ CORRECTION: Charger les classes quand le niveau change (ou au mount)
  useEffect(() => {
    if (schoolId && selectedAcademicYearId) {
      // Ne pas recharger pour les enseignants (déjà chargé dans loadInitialData)
      if (!isTeacherRole) {
        loadAllowedClasses();
      }
    }
  }, [loadAllowedClasses, schoolId, selectedAcademicYearId, isTeacherRole]);

  useEffect(() => {
    // Recharger les classes quand le niveau change (filtrage côté client ou serveur)
    if (selectedAcademicYearId) {
      loadClassesFiltered();
    }
  }, [loadClassesFiltered, selectedAcademicYearId, selectedLevel]);

  useEffect(() => { 
    // Ce useEffect gère uniquement le cas showAllStudents / selectedClass vide
    if (!selectedClass) {
      if (showAllStudents) { setStudents(allStudents); }
      else { setStudents([]); }
    }
  }, [selectedClass, showAllStudents, allStudents]);

  useEffect(() => {
    if (selectedAcademicYearId) {
      loadClassSubjects();
    }
  }, [loadClassSubjects, selectedClass, selectedAcademicYearId]);



  const currentSequence = useMemo(() => {
    return sequences.find(s => s.id.toString() === selectedSequence);
  }, [sequences, selectedSequence]);

  // ═══ V9 : trimestre ACTIF = trimestre de la séquence sélectionnée ═══
  // La séquence ouverte appartient à une période (T1/T2/T3). Seules les
  // compétences de CE trimestre doivent s'afficher — avant, les compétences
  // des 3 trimestres étaient toutes listées.
  const activeTrimester = useMemo((): number | null => {
    if (!currentSequence?.evaluation_period_id) return null;
    const period = evaluationPeriods.find(p => p.id === currentSequence.evaluation_period_id);
    if (!period) return null;
    const m = (period.code || '').toUpperCase().match(/T\s*(\d)/);
    if (m) return parseInt(m[1], 10);
    // Repli : order_index de la période (1=T1, 2=T2…)
    return (period as any).order_index ?? null;
  }, [currentSequence, evaluationPeriods]);

  // Trimestre d'une compétence : champ API direct, sinon parsé depuis le nom
  // "ANGLAIS TRIMESTRE 2 COMPETENCE 3", sinon 1.
  const getCompTrimester = useCallback((c: any): number => {
    if (c?.trimester !== undefined && c?.trimester !== null && Number(c.trimester) > 0) return Number(c.trimester);
    const parsed = parseFirstCycleSubject(c?.subject_name || c?.subjectName || '');
    return parsed.trimester || 1;
  }, []);

  // ✅ Filtrer les compétences quand la matière change (1er cycle uniquement)
  useEffect(() => {
    if (!isFirstCycle || !selectedSubject) {
      setCompetencies([]);
      setSelectedCompetence("");
      return;
    }
    // Filtrer par matière ET par trimestre actif (celui de la séquence)
    const filtered = firstCycleRawCompetencies
      .filter((c: any) => parseFirstCycleSubject(c.subject_name || c.subjectName || '').baseName === selectedSubject)
      .filter((c: any) => activeTrimester === null || getCompTrimester(c) === activeTrimester)
      .map((c: any) => {
        const baseName = parseFirstCycleSubject(c.subject_name || c.subjectName || '').baseName
        const key = (baseName || '').toUpperCase().replace(/\s+/g, ' ').trim()
        const coefEntry = classSubjects.find((s: any) =>
          ((s.label_fr || s.name || '').toUpperCase().replace(/\s+/g, ' ').trim()) === key
        )
        return {
          id: c.id,
          subject_id: c.subject_id || c.subject_specialty_id,
          trimester: getCompTrimester(c),
          number: c.number || parseFirstCycleSubject(c.subject_name || c.subjectName || '').competenceNumber || 1,
          label_fr: c.label_fr || `Compétence ${c.number || 1}`,
          label_en: c.label_en,
          description: c.description || c.details || c.description_fr || '',
          max_score: 20,
          coefficient: coefEntry?.coefficient ?? 1,
        }
      })
      .sort((a: Competence, b: Competence) => {
        if (a.trimester !== b.trimester) return a.trimester - b.trimester;
        return a.number - b.number;
      });

    console.log(`[GradesPage] Compétences "${selectedSubject}" — trimestre actif T${activeTrimester ?? '?'}:`, filtered.length);
    setCompetencies(filtered);
    if (filtered.length === 0) setSelectedCompetence("");
  }, [isFirstCycle, selectedSubject, firstCycleRawCompetencies, classSubjects, activeTrimester, getCompTrimester]);

  useEffect(() => { 
    if (schoolId && selectedAcademicYearId) {
      loadGrades(); 
    }
  }, [loadGrades, schoolId, refreshCounter, selectedAcademicYearId]);

  useEffect(() => {
    if (schoolId && selectedAcademicYearId) {
      loadSubjectsStatus();
    }
  }, [loadSubjectsStatus, schoolId, refreshCounter, selectedAcademicYearId]);

  const openSequences = useMemo(() => sequences.filter(s => s.is_open), [sequences]);

  const _availableSubjects = useMemo(() => {
    if (isAdminRole || isCenseurRole) return allSubjects;
    if (isTeacherRole && teacherSubjectIds.length > 0) {
      return allSubjects.filter(s => teacherSubjectIds.includes(s.id));
    }
    return allSubjects;
  }, [allSubjects, teacherSubjectIds, isAdminRole, isCenseurRole, isTeacherRole]);

  // ✅ CORRECTION: availableClasses utilise maintenant les classes filtrées par l'API
  const availableClasses = useMemo(() => {
    let result = classes;

    // Filtrer par niveau côté client seulement si les données ont level_id
    // et que l'API n'a pas déjà filtré (fallback)
    const hasLevelIds = classes.length > 0 && classes.some(c => c.level_id !== undefined && c.level_id !== null);
    if (selectedLevel && hasLevelIds) {
      const levelId = parseInt(selectedLevel);
      const beforeFilter = result.length;
      result = result.filter(c => c.level_id === levelId);
      console.log(`[GradesPage] Client-side level filter: ${beforeFilter} -> ${result.length} for level_id=${levelId}`);
    }

    if (isAdminRole || isCenseurRole) return result;
    if (isTeacherRole) {
      if (!assignmentsLoaded) {
        // Pas encore chargé: montrer toutes les classes filtrées par niveau (temporairement)
        return result;
      }
      // Chargé: filtrer strictement par allowedClassIds (même si vide)
      return result.filter(c => allowedClassIds.includes(c.id));
    }
    return result;
  }, [classes, allowedClassIds, assignmentsLoaded, isAdminRole, isTeacherRole, isCenseurRole, selectedLevel]);


  const displaySubjects = useMemo(() => {
    let result: Subject[] = [];

    if (!selectedClass) {
      // Pas de classe sélectionnée: montrer les matières de l'enseignant (ou toutes pour admin)
      result = isTeacherRole && teacherSubjectIds.length > 0
        ? allSubjects.filter(s => teacherSubjectIds.includes(s.id))
        : allSubjects;
    } else if (isFirstCycle) {
      // ✅ 1er cycle : les matières sont déduites des compétences chargées
      // baseName -> subject_id (association robuste par id, repli par nom)
      const compSubjects = new Map<string, number>()
      firstCycleRawCompetencies.forEach((c: any) => {
        const base = parseFirstCycleSubject(c.subject_name || c.subjectName || '').baseName
        if (base && !compSubjects.has(base)) compSubjects.set(base, c.subject_id || 0)
      })

      console.log(`[GradesPage] First cycle groups from competencies:`, Array.from(compSubjects.keys()));

      // Coefficients officiels depuis classSubjects (subject-specialties du niveau :
      // spécialité de la classe + spécialité "Générale")
      const normalizeName = (n: string) => (n || '').toUpperCase().replace(/\s+/g, ' ').trim()
      const coefByName = new Map<string, number>()
      const coefById = new Map<number, number>()
      classSubjects.forEach((s: Subject) => {
        const keys = [s.label_fr, s.name, s.code].filter(Boolean).map((v: any) => normalizeName(v))
        keys.forEach(k => { if (k && !coefByName.has(k)) coefByName.set(k, s.coefficient) })
        if (s.id > 0 && !coefById.has(s.id)) coefById.set(s.id, s.coefficient)
      })
      let names = Array.from(compSubjects.keys())
      if (classSubjects.length > 0) {
        // Ne garder que les matières de la classe (Générale + spécialité de la classe)
        const before = names.length
        names = names.filter((n) => {
          const sid = compSubjects.get(n) || 0
          return coefById.has(sid) || coefByName.has(normalizeName(n))
        })
        console.log(`[GradesPage] First cycle subjects filtered by classSubjects: ${before} -> ${names.length}`)
      }
      // ✅ V10 : enseignant = uniquement les matières qui lui sont ASSIGNÉES
      // dans CETTE classe (même règle que le 2nd cycle — avant ce fix, un
      // prof voyait TOUTES les matières du 1er cycle).
      if (isTeacherRole && teacherAssignments.length > 0 && selectedClass) {
        const assignedIds = teacherAssignments
          .filter(a => a.class_id === parseInt(selectedClass))
          .map(a => a.subject_id)
        const before = names.length
        names = names.filter(n => assignedIds.includes(compSubjects.get(n) || 0))
        console.log(`[GradesPage] First cycle subjects filtered by teacher assignments: ${before} -> ${names.length}`)
      }
      result = names.map((baseName) => ({
        id: -1,
        name: baseName,
        label_fr: baseName,
        label_en: baseName,
        coefficient: coefById.get(compSubjects.get(baseName) || 0) ?? coefByName.get(normalizeName(baseName)) ?? 1,
      }));
    } else if (isTeacherRole && teacherAssignments.length > 0) {
      // ✅ Enseignant: ne montrer que les matières assignées à cette classe
      const classSubjectIds = teacherAssignments
        .filter(a => a.class_id.toString() === selectedClass)
        .map(a => a.subject_id);
      if (classSubjectIds.length > 0) {
        // Intersection: matières du backend ET assignées à l'enseignant
        const sourceSubjects = classSubjects.length > 0 ? classSubjects : allSubjects;
        result = sourceSubjects.filter(s => classSubjectIds.includes(s.id));
      } else {
        result = [];
      }
    } else if (classSubjects.length > 0) {
      // Admin/censeur: matières du backend pour cette classe
      result = classSubjects;
    } else {
      result = allSubjects;
    }

    // ✅ Dédupliquer par id — ou par NOM pour les matières virtuelles du 1er cycle (id = -1)
    const seen = new Set<string>();
    return result.filter(s => {
      const key = s.id === -1 ? `name:${s.name}` : `id:${s.id}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [allSubjects, classSubjects, selectedClass, teacherAssignments, isTeacherRole, teacherSubjectIds, isFirstCycle, firstCycleRawCompetencies]);

  const filteredStudents = useMemo(() => {
    if (!searchQuery) return students;
    const q = searchQuery.toLowerCase();
    return students.filter(s =>
      (s.first_name?.toLowerCase().includes(q)) ||
      (s.last_name?.toLowerCase().includes(q)) ||
      (s.full_name?.toLowerCase().includes(q)) ||
      (s.matricule?.toLowerCase().includes(q))
    );
  }, [students, searchQuery]);




  // ✅ Affichage « NOM Prénom » (convention des listes définitives).
  // Gère aussi les élèves à champ unique (nom complet dans last_name) :
  // si un seul champ est renseigné, on l'affiche tel quel.
  const displayName = useCallback((s: Student): string => {
    const last = (s.last_name || '').trim();
    const first = (s.first_name || '').trim();
    if (last && first) return `${last} ${first}`;
    return last || first || (s.full_name || '').trim();
  }, []);

  const sortedStudents = useMemo(() => {
    let sorted = [...filteredStudents];
    if (sortBy === "name") {
      sorted.sort((a, b) => {
        // Tri sur NOM puis prénom — comme les listes officielles
        const nameA = displayName(a).toLowerCase();
        const nameB = displayName(b).toLowerCase();
        return sortOrder === "asc" ? nameA.localeCompare(nameB, "fr") : nameB.localeCompare(nameA, "fr");
      });
    } else if (sortBy === "grade") {
      sorted.sort((a, b) => {
        const gradeA = getExistingGrade(a.id)?.value ?? -1;
        const gradeB = getExistingGrade(b.id)?.value ?? -1;
        return sortOrder === "asc" ? gradeA - gradeB : gradeB - gradeA;
      });
    }
    return sorted;
  }, [filteredStudents, sortBy, sortOrder, grades, displayName]);

  // ==================== HANDLERS ====================

  const handleSort = (field: "name" | "grade") => {
    if (sortBy === field) {
      setSortOrder(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleGradeChange = (studentId: number, value: string) => {
    let numValue = value === "" ? null : parseFloat(value);
    // Note strictement bornée 0..20 — toute valeur hors plage est rejetée
    if (numValue !== null && (isNaN(numValue) || numValue < 0 || numValue > 20)) {
      toast.error(lang === 'fr' ? "La note doit être comprise entre 0 et 20" : "Grade must be between 0 and 20");
      return;
    }
    const appreciation = getAppreciation(numValue, lang);
    setEntryGrades(prev => ({
      ...prev,
      [studentId]: { student_id: studentId, value: numValue, appreciation, comment: prev[studentId]?.comment || "" }
    }));
  };

  const handleCommentChange = (studentId: number, comment: string) => {
    setEntryGrades(prev => ({ ...prev, [studentId]: { ...prev[studentId], student_id: studentId, comment } }));
  };

  const handleEditGrade = (gradeId: number, value: number) => {
    const appreciation = getAppreciation(value, lang);
    setEditedGrades(prev => ({ ...prev, [gradeId]: { value, appreciation, comment: prev[gradeId]?.comment || "" } }));
  };

  const getExistingGrade = (studentId: number): Grade | undefined => {
    return grades.find(g => g.student_id === studentId);
  };

  const getEntryValue = (studentId: number): string => {
    const entry = entryGrades[studentId];
    if (entry && entry.value !== null) return entry.value.toString();
    const existing = getExistingGrade(studentId);
    if (existing && !editedGrades[existing.id]) return existing.value?.toString() || "";
    return "";
  };

  const _getEntryAppreciation = (studentId: number): string => {
    const entry = entryGrades[studentId];
    if (entry && entry.appreciation) return entry.appreciation;
    const existing = getExistingGrade(studentId);
    if (existing && !editedGrades[existing.id]) return existing.appreciation_fr || existing.appreciation_en || getAppreciation(existing.value, lang) || "";
    return "";
  };

  const handleBulkSave = async () => {
    const entries = Object.values(entryGrades).filter(e => e.value !== null && !isNaN(e.value));
    if (entries.length === 0) {
      toast.error(lang === "fr" ? "Aucune note a sauvegarder" : "No grades to save");
      return;
    }
    if (currentSequence && !currentSequence.is_open) {
      toast.error(lang === "fr" ? "Cette sequence est fermee" : "This sequence is closed");
      return;
    }
    if (!selectedAcademicYearId) {
      toast.error(lang === "fr" ? "Aucune annee scolaire selectionnee" : "No academic year selected");
      return;
    }

    const effectiveTeacherId = teacherId || ((auth.user as any)?.id ?? 1);

    setSaving(true);
    try {
      // Déterminer le vrai subject_id (1er cycle = celui de la compétence, 2nd cycle = selectedSubject)
      let effectiveSubjectId: number;
      if (isFirstCycle && selectedCompetence) {
        const comp = competencies.find(c => c.id.toString() === selectedCompetence);
        effectiveSubjectId = comp?.subject_id ?? 0;
      } else {
        effectiveSubjectId = parseInt(selectedSubject);
      }

      // ✅ Coefficient de LA CLASSE (pas le coefficient global de la matière)
      const classCoef = (!isFirstCycle && effectiveSubjectId)
        ? (displaySubjects.find(s => s.id === effectiveSubjectId)?.coefficient ?? 1)
        : 1;

      const bulkData = {
        class_id: parseInt(selectedClass),
        subject_id: effectiveSubjectId,
        sequence_id: parseInt(selectedSequence),
        academic_year_id: selectedAcademicYearId,
        school_id: schoolId,
        teacher_id: effectiveTeacherId,
        grades: entries.map(e => {
          const base: any = {
            student_id: e.student_id,
            score: e.value,
            max_score: 20,
            coefficient: classCoef,
            appreciation_fr: e.appreciation,
            appreciation_en: getAppreciation(e.value, "en"),
            class_id: parseInt(selectedClass),
            subject_id: effectiveSubjectId,
            sequence_id: parseInt(selectedSequence),
            teacher_id: effectiveTeacherId,
            academic_year_id: selectedAcademicYearId,
            school_id: schoolId,
          };
          if (isFirstCycle && selectedCompetence) {
            base.competence_id = parseInt(selectedCompetence);
          }
          return base;
        })
      };

      const res = await apiService.post("/grades/bulk", bulkData);
      const result = (res as any)?.data ?? res;

      const created = (result as any)?.created ?? 0;
      const updated = (result as any)?.updated ?? 0;

      if (created === 0 && updated === 0) {
        toast.warning(lang === "fr" 
          ? "Aucune note creee/mise a jour. Causes possibles: (1) sequence fermee, (2) note deja existante sans UPSERT backend, (3) teacher_id invalide." 
          : "No grades created/updated. Possible causes: (1) sequence closed, (2) grade already exists without backend UPSERT, (3) invalid teacher_id.");
      } else {
        toast.success(`${created} ${lang === "fr" ? "creees" : "created"}, ${updated} ${lang === "fr" ? "mises a jour" : "updated"}`);
      }

      setEntryGrades({});
      setEntryMode(false);
      loadGrades();
      loadSubjectsStatus();
    } catch (err: any) {
      const msg = getErrorDetail(err, t.saveError);
      toast.error(msg);
      console.error("Bulk save error:", err);
    } finally { setSaving(false); }
  };

  const handleUpdateGrade = async (gradeId: number) => {
    const edit = editedGrades[gradeId];
    if (!edit) return;
    try {
      await apiService.put(`/grades/${gradeId}`, {
        score: edit.value,
        appreciation_fr: edit.appreciation,
        appreciation_en: getAppreciation(edit.value, "en"),
      });
      toast.success(t.success);
      setEditedGrades(prev => { const next = { ...prev }; delete next[gradeId]; return next; });
      loadGrades();
      loadSubjectsStatus();
    } catch (err: any) {
      toast.error(getErrorDetail(err, t.error));
    }
  };

  const handleDeleteGrade = async (gradeId: number) => {
    try {
      await apiService.delete(`/grades/${gradeId}`);
      toast.success(t.success);
      setShowDeleteConfirm(null);
      loadGrades();
      loadSubjectsStatus();
    } catch (err: any) {
      toast.error(getErrorDetail(err, t.error));
    }
  };

  const handleToggleSequence = async (id: number) => {
    try {
      await apiService.patch(`/grades/sequences/${id}/toggle`, {});
      toast.success(lang === "fr" ? "Sequence mise a jour" : "Sequence updated");
      loadInitialData();
    } catch (err) { toast.error(t.error); }
  };

  // ==================== EXPORT/IMPORT ====================

  // ═══ FICHE DE SAISIE DES NOTES (impression/téléchargement) ═══
  // Chaque enseignant imprime SA fiche : classe + matière (+ compétence) +
  // séquence courantes, élèves triés, case note vide à remplir à la main.
  const handlePrintFiche = () => {
    if (!selectedClass || !selectedSequence) {
      toast.error(lang === 'fr' ? 'Sélectionnez au moins une classe et une séquence' : 'Select at least a class and a sequence');
      return;
    }
    let hdr: any = null;
    try { hdr = JSON.parse(localStorage.getItem('lybibal-school-header') || 'null'); } catch { hdr = null; }
    const H = hdr || {
      republic_fr: 'RÉPUBLIQUE DU CAMEROUN', republic_en: 'REPUBLIC OF CAMEROON',
      motto_fr: 'Paix-Travail-Patrie', motto_en: 'Peace-Work-Fatherland',
      region: "RÉGION DE L'OUEST", department: 'DÉPARTEMENT DE LA MIFI',
      delegation: 'DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES',
      sub_division: 'ARRONDISSEMENT DE BAFOUSSAM II',
      school_name: 'LYCÉE BILINGUE DE BALENG', school_name_en: 'GBHS BALENG',
      address: 'BP : 1041 Baleng', po_box: 'P.O. Box : 1041 Baleng', logo_url: '',
    };
    const clsName = classes.find(c => c.id.toString() === selectedClass)?.name || '—';
    const seqName = sequences.find(sq => sq.id.toString() === selectedSequence)?.name || '—';
    const subjObj = displaySubjects.find(sb => (isFirstCycle ? sb.name : sb.id.toString()) === selectedSubject);
    const subjName = subjObj ? getSubjectDisplayName(subjObj, lang) : (isFirstCycle ? selectedSubject : '—');
    const compLabel = isFirstCycle && selectedCompetence
      ? competencies.find(c => c.id.toString() === selectedCompetence)?.label_fr : null;
    const coef = subjObj?.coefficient ?? 1;
    // L'enseignant = l'utilisateur connecté (le profil teacher n'expose pas de nom ici)
    const teacherDisplay = `${(auth.user as any)?.first_name || ''} ${(auth.user as any)?.last_name || ''}`.trim() || '—';
    const yearName = currentAcademicYear?.name || String(selectedAcademicYearId || '');
    const today = new Date().toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB');

    const rowsHTML = sortedStudents.map((st, i) => `
      <tr style="${i % 2 === 1 ? 'background:#f9fafb;' : ''}page-break-inside:avoid;">
        <td style="border:1px solid #000;padding:4px 6px;text-align:center;width:6%;">${i + 1}</td>
        <td style="border:1px solid #000;padding:4px 6px;width:16%;font-family:monospace;font-size:9px;">${st.matricule || '—'}</td>
        <td style="border:1px solid #000;padding:4px 6px;font-weight:600;">${st.last_name || ''} ${st.first_name || ''}</td>
        <td style="border:1px solid #000;padding:4px 6px;width:14%;"></td>
        <td style="border:1px solid #000;padding:4px 6px;width:18%;"></td>
      </tr>`).join('');

    const logoHtml = H.logo_url
      ? `<img src="${H.logo_url}" style="width:50px;height:50px;object-fit:contain;" />`
      : `<div style="width:48px;height:48px;background:#1e3a5f;border-radius:50%;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:12px;font-weight:bold;">LB</span></div>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Fiche de saisie — ${clsName}</title>
      <style>*{-webkit-print-color-adjust:exact;print-color-adjust:exact}@page{size:A4 portrait;margin:10mm}body{font-family:Arial,sans-serif;margin:0;padding:0;font-size:11px}
      table{width:100%;border-collapse:collapse;font-size:10px}thead{display:table-header-group}tr{page-break-inside:avoid}</style>
    </head><body>
      <div style="text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:8px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;text-align:left;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${H.republic_fr}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${H.motto_fr}</p>
            <p style="margin:4px 0 1px;font-size:9px;">${H.region}</p>
            <p style="margin:1px 0;font-size:9px;">${H.department}</p>
            <p style="margin:1px 0;font-size:9px;">${H.delegation}</p>
            <p style="margin:1px 0;font-size:9px;">${H.sub_division}</p>
          </div>
          <div style="width:70px;text-align:center;margin:0 12px;flex-shrink:0;">${logoHtml}</div>
          <div style="flex:1;text-align:right;">
            <p style="margin:0;font-size:11px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${H.republic_en}</p>
            <p style="margin:2px 0;font-size:9px;color:#555;">${H.motto_en}</p>
            <p style="margin:4px 0 1px;font-size:9px;">${H.region.replace("RÉGION DE L'", 'WEST ').replace('RÉGION', 'REGION')}</p>
            <p style="margin:1px 0;font-size:9px;">${H.department.replace('DÉPARTEMENT DE LA ', '').replace('DÉPARTEMENT', 'DIVISION')}</p>
            <p style="margin:1px 0;font-size:9px;">${H.delegation.replace('DÉLÉGATION', 'DELEGATION')}</p>
            <p style="margin:1px 0;font-size:9px;">${H.sub_division.replace('ARRONDISSEMENT', 'SUB DIVISION')}</p>
          </div>
        </div>
        <div style="margin-top:6px;">
          <p style="margin:0;font-size:14px;font-weight:bold;color:#1e3a5f;">${H.school_name}</p>
          <p style="margin:2px 0;font-size:11px;font-weight:600;color:#1e3a5f;">${H.school_name_en}</p>
          <p style="margin:2px 0;font-size:9px;color:#666;">${H.address} | ${H.po_box}</p>
        </div>
      </div>
      <h1 style="text-align:center;font-size:20px;font-weight:bold;color:#1e3a5f;margin:10px 0;text-transform:uppercase;letter-spacing:1px;">FICHE DE SAISIE DES NOTES</h1>
      <div style="display:flex;justify-content:space-between;flex-wrap:wrap;gap:6px;margin-bottom:10px;font-size:11px;">
        <div><strong>${lang === 'fr' ? 'Année académique' : 'Academic year'}:</strong> ${yearName}</div>
        <div><strong>${lang === 'fr' ? 'Classe' : 'Class'}:</strong> ${clsName}</div>
        <div><strong>${lang === 'fr' ? 'Enseignant' : 'Teacher'}:</strong> ${teacherDisplay}</div>
        <div><strong>${lang === 'fr' ? 'Matière' : 'Subject'}:</strong> ${subjName}${compLabel ? ' — ' + compLabel : ''}</div>
        <div><strong>${lang === 'fr' ? 'Séquence' : 'Sequence'}:</strong> ${seqName}</div>
        <div><strong>Coef:</strong> ${coef}</div>
      </div>
      <table>
        <thead>
          <tr style="background:#1e3a5f;color:#fff;">
            <th style="border:1px solid #000;padding:6px;text-align:center;">N°</th>
            <th style="border:1px solid #000;padding:6px;text-align:center;">${lang === 'fr' ? 'Matricule' : 'ID'}</th>
            <th style="border:1px solid #000;padding:6px;text-align:center;">${lang === 'fr' ? 'Nom(s) et prénom(s)' : 'Name(s)'}</th>
            <th style="border:1px solid #000;padding:6px;text-align:center;">${lang === 'fr' ? 'Note /20' : 'Grade /20'}</th>
            <th style="border:1px solid #000;padding:6px;text-align:center;">${lang === 'fr' ? 'Observations' : 'Observations'}</th>
          </tr>
        </thead>
        <tbody>${rowsHTML}</tbody>
      </table>
      <div style="margin-top:30px;display:flex;justify-content:flex-end;">
        <div style="text-align:center;margin-right:40px;">
          <p style="margin:0;font-size:10px;">Fait à Baleng, le ${today}</p>
          <p style="margin:30px 0 0;font-size:10px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${lang === 'fr' ? "Visa de l'enseignant" : "Teacher's signature"}</p>
        </div>
      </div>
      <div style="margin-top:20px;text-align:center;font-size:9px;color:#999;border-top:1px solid #eee;padding-top:6px;">
        myhelperschool.cm — Téléchargé sur https://myhelperschool.cm/
      </div>
    </body></html>`;

    const pw = window.open('', '_blank');
    if (!pw) { toast.error(lang === 'fr' ? 'Autorisez les popups' : 'Allow popups'); return; }
    pw.document.write(html);
    pw.document.close();
    pw.focus();
    pw.print();
  };

  const handleExportExcel = () => {
    if (!selectedClass || !selectedSubject || !selectedSequence) {
      toast.error(lang === "fr" ? "Selectionnez classe, matiere et sequence" : "Select class, subject and sequence");
      return;
    }
    const subjectObj = displaySubjects.find(s => s.id.toString() === selectedSubject);
    const subjectName = subjectObj ? getSubjectDisplayName(subjectObj, lang) : "Matiere";
    const className = classes.find(c => c.id.toString() === selectedClass)?.name || "Classe";
    const sequenceName = sequences.find(s => s.id.toString() === selectedSequence)?.name || "Sequence";

    const headers = ["Matricule", "Nom", "Prenom", "Note (0-20)", "Appreciation", "Commentaire"];
    const rows = filteredStudents.map(s => [s.matricule, s.last_name, s.first_name, "", "", ""]);
    const csvContent = [headers.join(";"), ...rows.map(r => r.join(";"))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `notes_${className}_${subjectName}_${sequenceName}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(t.exportSuccess);
  };

  const handleImportExcel = async () => {
    if (!importFile) { toast.error(t.fileRequired); return; }
    if (!selectedClass || !selectedSubject || !selectedSequence) {
      toast.error(lang === "fr" ? "Selectionnez classe, matiere et sequence avant l'import" : "Select class, subject and sequence before import");
      return;
    }
    if (!selectedAcademicYearId) {
      toast.error(lang === "fr" ? "Aucune annee scolaire selectionnee" : "No academic year selected");
      return;
    }
    setImportLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      formData.append("class_id", selectedClass);
      // ✅ 1er cycle : le sélecteur porte le NOM de la matière (id -1) — on envoie
      // l'id réel de la matière via la compétence sélectionnée, sinon 422.
      let importSubjectId = selectedSubject;
      if (isFirstCycle && selectedCompetence) {
        const comp = competencies.find(c => c.id.toString() === selectedCompetence);
        if (comp?.subject_id) importSubjectId = String(comp.subject_id);
      }
      if (!importSubjectId || importSubjectId === '' || isNaN(Number(importSubjectId))) {
        toast.error(lang === 'fr' ? "Matière invalide pour l'import (identifiant numérique requis)" : "Invalid subject for import");
        setImportLoading(false);
        return;
      }
      formData.append("subject_id", importSubjectId);
      formData.append("sequence_id", selectedSequence);
      formData.append("academic_year_id", selectedAcademicYearId.toString());
      if (schoolId) formData.append("school_id", schoolId.toString());

      const res = await apiService.post("/grades/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const result = (res as any)?.data ?? res;
      const created = result.created || 0;
      const updated = result.updated || 0;

      if (created === 0 && updated === 0) {
        toast.warning(lang === "fr" ? "Aucune note importee" : "No grades imported");
      } else {
        toast.success(`Import: ${created} ${lang === "fr" ? "creees" : "created"}, ${updated} ${lang === "fr" ? "mises a jour" : "updated"}`);
      }

      setShowImportModal(false);
      setImportFile(null);
      loadGrades();
      loadSubjectsStatus();
    } catch (err: any) {
      const status = err.response?.status;
      const msg = getErrorDetail(err, t.importError);
      if (status === 405) {
        toast.error("Erreur 405: Endpoint /grades/import non disponible. Verifiez le backend.");
      } else { toast.error(msg); }
      console.error("Import error:", err);
    } finally { setImportLoading(false); }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith(".csv") || file.name.endsWith(".xlsx") || file.name.endsWith(".xls"))) {
      setImportFile(file);
    } else { toast.error(t.invalidFile); }
  };

  // ─── VUE MOBILE : cartes élèves (la colonne Note est hors écran sur
  // téléphone dans le tableau 7 colonnes → un élève = une carte lisible) ───
  const renderMobileStudentCard = (student: Student) => {
    const existingGrade = getExistingGrade(student.id);
    const editedGrade = existingGrade ? editedGrades[existingGrade.id] : null;
    return (
      <div key={student.id} className={`p-4 space-y-2 ${entryMode ? 'bg-amber-50/30' : 'hover:bg-gray-50'}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center text-xs font-bold text-[#1e3a8a] shrink-0">
              {(student.last_name?.[0] || "") + (student.first_name?.[0] || "")}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{displayName(student)}</p>
              <p className="text-xs text-gray-500 truncate">
                {student.matricule} · {student.class_name || classes.find(c => c.id === student.class_id)?.name || "-"}
              </p>
            </div>
          </div>
          <div className="shrink-0">
            {entryMode ? (
              <input
                type="number" min={0} max={20} step={0.25}
                value={entryGrades[student.id]?.value !== undefined ? entryGrades[student.id]?.value ?? "" : existingGrade?.value ?? ""}
                onChange={(e) => handleGradeChange(student.id, e.target.value)}
                className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-center font-bold text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                placeholder={t.gradePlaceholder}
              />
            ) : existingGrade ? (
              editedGrade ? (
                <input
                  type="number" min={0} max={20} step={0.25} value={editedGrade.value}
                  onChange={(e) => handleEditGrade(existingGrade.id, parseFloat(e.target.value))}
                  className="w-20 px-2 py-1.5 border border-gray-300 rounded-lg text-center font-bold text-sm focus:ring-2 focus:ring-[#1e3a8a]"
                />
              ) : (
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getAppreciationBadgeColor(existingGrade.value)}`}>
                  {existingGrade.value?.toFixed(2) ?? "-"}
                </span>
              )
            ) : (
              <span className="text-sm text-gray-400">-</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">{t.appreciation}:</span>
          {entryMode ? (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getAppreciationBadgeColor(entryGrades[student.id]?.value ?? null)}`}>
              {entryGrades[student.id]?.appreciation || getAppreciation(entryGrades[student.id]?.value ?? null, lang) || "-"}
            </span>
          ) : existingGrade ? (
            editedGrade ? (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getAppreciationBadgeColor(editedGrade.value)}`}>{editedGrade.appreciation}</span>
            ) : (
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getAppreciationBadgeColor(existingGrade.value)}`}>
                {existingGrade.appreciation_fr || existingGrade.appreciation_en || getAppreciation(existingGrade.value, lang) || "-"}
              </span>
            )
          ) : (
            <span className="text-xs text-gray-400">-</span>
          )}
        </div>

        {entryMode ? (
          <input
            type="text"
            value={entryGrades[student.id]?.comment || ""}
            onChange={(e) => handleCommentChange(student.id, e.target.value)}
            placeholder={t.optionalComment}
            className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]"
          />
        ) : (
          existingGrade?.comment && <p className="text-xs text-gray-500">{existingGrade.comment}</p>
        )}

        {!entryMode && existingGrade && (
          <div className="flex items-center gap-1 pt-1">
            {editedGrade ? (
              <>
                <button onClick={() => handleUpdateGrade(existingGrade.id)} className="px-2.5 py-1 bg-green-600 text-white rounded-lg text-xs font-medium">{t.save}</button>
                <button onClick={() => setEditedGrades(prev => { const next = { ...prev }; delete next[existingGrade.id]; return next; })} className="px-2.5 py-1 border border-gray-300 rounded-lg text-xs text-gray-600">{t.cancel}</button>
              </>
            ) : (
              <>
                <button onClick={() => handleEditGrade(existingGrade.id, existingGrade.value || 0)} className="px-2.5 py-1 border border-gray-300 rounded-lg text-xs text-[#1e3a8a]">{t.edit}</button>
                <button onClick={() => setShowDeleteConfirm(existingGrade.id)} className="px-2.5 py-1 border border-gray-300 rounded-lg text-xs text-red-600">{t.delete}</button>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // ==================== RENDER ====================

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{lang === "fr" ? "Annee scolaire requise" : "Academic year required"}</h2>
          <p className="text-sm text-gray-500">{lang === "fr" ? "Veuillez selectionner une annee scolaire dans le header pour acceder aux notes." : "Please select an academic year in the header to access grades."}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <BookOpen className="w-7 h-7 text-[#1e3a8a]" />
              {t.title}
            </h1>
            <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
            {currentAcademicYear && (
              <div className="flex items-center gap-2 mt-2">
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                  {currentAcademicYear.name}
                </span>
                <span className="text-xs text-gray-400">
                  school_id: {schoolId || "-"}
                </span>
              </div>
            )}
          </div>
          {canManageSequences && (
            <div className="flex items-center gap-2">
              <button onClick={() => setShowSequenceModal(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm font-medium transition-colors">
                <Calendar className="w-4 h-4" />
                {t.sequences}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-center gap-3">

            {/* ✅ NOUVEAU: Section Select (caché pour les enseignants car pas de level_id sur classes) */}
            {!isTeacherRole && (
              <>
            <div className="relative">
              <select 
                value={selectedSection} 
                onChange={(e) => {
                  setSelectedSection(e.target.value);
                  setSelectedCycle("");
                  setSelectedLevel("");
                  setSelectedClass("");
                  setSelectedSubject("");
                  setEntryGrades({});
                  setGrades([]);
                }}
                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[180px]"
              >
                <option value="">{t.selectSection}</option>
                {sections.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
              </select>
              <School className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* ✅ NOUVEAU: Cycle Select */}
            <div className="relative">
              <select 
                value={selectedCycle} 
                onChange={(e) => {
                  setSelectedCycle(e.target.value);
                  setSelectedLevel("");
                  setSelectedClass("");
                  setSelectedSubject("");
                  setEntryGrades({});
                  setGrades([]);
                }} 
                disabled={!selectedSection}
                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[180px] disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t.selectCycle}</option>
                {cycles.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
              </select>
              <Layers className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* ✅ NOUVEAU: Level Select */}
            <div className="relative">
              <select 
                value={selectedLevel} 
                onChange={(e) => {
                  setSelectedLevel(e.target.value);
                  setSelectedClass("");
                  setSelectedSubject("");
                  setEntryGrades({});
                  setGrades([]);
                }} 
                disabled={!selectedCycle}
                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[180px] disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t.selectLevel}</option>
                {levels.map((l) => (<option key={l.id} value={l.id}>{l.name}</option>))}
              </select>
              <BarChart3 className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>
              </>
            )}

            {/* Sequence Select */}
            <div className="relative">
              <select value={selectedSequence} onChange={(e) => {
                  setSelectedSequence(e.target.value);
                  const seq = sequences.find(s => s.id.toString() === e.target.value);
                  if (seq && seq.evaluation_period_id) { setSelectedPeriod(seq.evaluation_period_id.toString()); }
                  setSelectedClass(""); setSelectedSubject(""); setEntryGrades({}); setGrades([]);
                }}
                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[220px]">
                <option value="">{t.selectSequence}</option>
                {openSequences.length === 0 ? (
                  <option value="" disabled>{t.noOpenSequences}</option>
                ) : (
                  evaluationPeriods.map(period => {
                    const periodOpenSeqs = openSequences.filter(s => s.evaluation_period_id === period.id);
                    if (periodOpenSeqs.length === 0) return null;
                    return (
                      <optgroup key={period.id} label={lang === "fr" ? period.label_fr : period.label_en}>
                        {periodOpenSeqs.map(seq => (
                          <option key={seq.id} value={seq.id}>{seq.name} {seq.code ? `(${seq.code})` : ""}</option>
                        ))}
                      </optgroup>
                    );
                  })
                )}
              </select>
              <Award className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Class Select */}
            <div className="relative">
              <select value={selectedClass} onChange={(e) => {
                  const newClassId = e.target.value;
                  setSelectedClass(newClassId);
                  setSelectedSubject("");
                  setEntryGrades({});
                  setGrades([]);
                  setClassSubjects([]);
                  if (newClassId) {
                    const classIdNum = parseInt(newClassId);
                    loadStudentsForClass(classIdNum);
                  } else {
                    setStudents([]);
                  }
                }} disabled={loadingClasses || (isTeacherRole && !assignmentsLoaded)}
                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[180px] disabled:bg-gray-100 disabled:cursor-not-allowed">
                <option value="">{t.selectClass}</option>
                {availableClasses.length === 0 && isTeacherRole && assignmentsLoaded && !assignmentsError && allowedClassIds.length === 0 ? (
                  <option value="" disabled>{lang === 'fr' ? 'Aucune classe assignée' : 'No assigned classes'}</option>
                ) : (
                  availableClasses.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))
                )}
              </select>
              <Users className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              {isTeacherRole && assignmentsError && (
                <span className="absolute -bottom-5 left-0 text-xs text-amber-600 whitespace-nowrap">
                  {lang === 'fr' ? '⚠️ Erreur chargement assignments' : '⚠️ Assignment loading error'}
                </span>
              )}
            </div>

            {/* Subject Select */}
            <div className="relative">
              <select 
                value={selectedSubject} 
                onChange={(e) => { 
                  setSelectedSubject(e.target.value); 
                  setSelectedCompetence(""); 
                }} 
                disabled={!selectedClass}
                className="pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a] bg-white min-w-[220px] disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">{t.selectSubject}</option>
                {displaySubjects.length === 0 && isFirstCycle ? (
                  <option value="" disabled>{lang === 'fr' ? '⚠️ Aucune matière trouvée pour cette classe' : '⚠️ No subjects found for this class'}</option>
                ) : (
                  displaySubjects.map((s) => (
                    <option key={s.id === -1 ? s.name : s.id} value={s.id === -1 ? s.name : s.id.toString()}>
                      {isFirstCycle ? s.name : getSubjectDisplayName(s, lang)} {s.coefficient ? `(coef. ${s.coefficient})` : ""}
                    </option>
                  ))
                )}
              </select>
              <BookOpen className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* ✅ Sélecteur de compétence (1er cycle uniquement) */}
            {isFirstCycle && selectedSubject && (
              <div className="relative">
                <select 
                  value={selectedCompetence} 
                  onChange={(e) => setSelectedCompetence(e.target.value)}
                  disabled={loadingCompetencies || competencies.length === 0}
                  className="pl-9 pr-4 py-2.5 border border-amber-400 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-amber-50 min-w-[280px] disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">{lang === 'fr' ? '⬇️ Choisir une compétence' : '⬇️ Select a competency'}</option>
                  {competencies.map((c) => (
                    <option key={c.id} value={c.id}>
                      T{c.trimester} C{c.number} : {c.label_fr} {c.coefficient ? `(coef. ${c.coefficient})` : ''}
                    </option>
                  ))}
                </select>
                <BookOpen className="w-4 h-4 text-amber-600 absolute left-3 top-1/2 -translate-y-1/2" />
                {loadingCompetencies && <Loader2 className="w-4 h-4 text-amber-600 absolute right-3 top-1/2 -translate-y-1/2 animate-spin" />}
              </div>
            )}

            {/* ── Description de la compétence sélectionnée (1er cycle) ── */}
            {isFirstCycle && selectedCompetence && (() => {
              const comp = competencies.find(c => c.id.toString() === selectedCompetence);
              if (!comp) return null;
              return (
                <div className="w-full mt-1 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <BookOpen className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-bold text-amber-900">
                      T{comp.trimester} · C{comp.number} — {comp.label_fr}
                      {comp.coefficient ? <span className="ml-1 font-medium text-amber-600">(coef. {comp.coefficient})</span> : null}
                    </p>
                    {comp.description && comp.description.trim() !== comp.label_fr?.trim() && (
                      <p className="text-xs text-amber-800 mt-1 whitespace-pre-wrap leading-relaxed">{comp.description}</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <input type="text" placeholder={t.searchStudent} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a]" />
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            </div>

            {/* Action Buttons */}
            {selectedClass && selectedSubject && selectedSequence && (
              <>
                <button onClick={() => {
                    setEntryMode(!entryMode);
                    if (!entryMode) {
                      const initial: Record<number, GradeEntry> = {};
                      sortedStudents.forEach(s => {
                        const existing = getExistingGrade(s.id);
                        if (existing) {
                          initial[s.id] = { student_id: s.id, value: existing.value, appreciation: existing.appreciation_fr || getAppreciation(existing.value, lang), comment: existing.comment || "" };
                        }
                      });
                      setEntryGrades(initial);
                    } else { setEntryGrades({}); }
                  }}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${entryMode ? "bg-amber-100 text-amber-700 hover:bg-amber-200" : "bg-[#1e3a8a] text-white hover:bg-[#152a5e]"}`}>
                  {entryMode ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
                  {entryMode ? t.exitEntryMode : t.entryMode}
                </button>

                {entryMode && (
                  <button onClick={handleBulkSave} disabled={saving}
                    className="px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {saving ? t.processing : t.save}
                  </button>
                )}

                <button onClick={handleExportExcel}
                  className="px-4 py-2.5 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 flex items-center gap-2 transition-colors shadow-sm">
                  <FileDown className="w-4 h-4" />{t.export}
                </button>

                <button onClick={() => setShowImportModal(true)}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 flex items-center gap-2 transition-colors shadow-sm">
                  <Upload className="w-4 h-4" />{t.import}
                </button>

                <button onClick={handlePrintFiche}
                  className="px-4 py-2.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 flex items-center gap-2 transition-colors shadow-sm"
                  title={lang === 'fr' ? 'Imprimer la fiche de saisie (classe + matière + séquence courantes)' : 'Print the grade entry sheet'}>
                  <Printer className="w-4 h-4" />{lang === 'fr' ? 'Fiche (PDF)' : 'Sheet (PDF)'}
                </button>
              </>
            )}
          </div>

          {/* ✅ NOUVEAU: Message si niveau sélectionné mais aucune classe */}
          {selectedLevel && !loadingClasses && classes.length > 0 && availableClasses.length === 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              <span className="text-sm text-amber-700">{t.noClassesForLevel}</span>
            </div>
          )}
          {selectedLevel && !loadingClasses && classes.length === 0 && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <span className="text-sm text-red-700">
                {lang === 'fr' 
                  ? 'Aucune classe chargée. Vérifiez la console (F12) pour voir la réponse de /classes.' 
                  : 'No classes loaded. Check console (F12) for /classes response.'}
              </span>
            </div>
          )}

          {/* Stats Bar */}
          {selectedClass && selectedSubject && selectedSequence && (
            <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center gap-1.5 text-xs"><Users className="w-3.5 h-3.5 text-gray-400" /><span className="text-gray-600">{stats.total} {t.totalStudents}</span></div>
              <div className="flex items-center gap-1.5 text-xs"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /><span className="text-emerald-600">{stats.entered} {t.gradesEntered}</span></div>
              <div className="flex items-center gap-1.5 text-xs"><XCircle className="w-3.5 h-3.5 text-orange-500" /><span className="text-orange-600">{stats.remaining} {t.remaining}</span></div>
              <div className="flex items-center gap-1.5 text-xs"><TrendingUp className="w-3.5 h-3.5 text-blue-500" /><span className="text-blue-600">{t.avg}: {stats.avg.toFixed(2)}</span></div>
              <div className="flex items-center gap-1.5 text-xs"><ArrowUpDown className="w-3.5 h-3.5 text-purple-500" /><span className="text-purple-600">{t.min}: {stats.min.toFixed(2)} | {t.max}: {stats.max.toFixed(2)}</span></div>
              {currentSequence && (
                <div className={`ml-auto flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${currentSequence.is_open ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                  {currentSequence.is_open ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {currentSequence.is_open ? t.sequenceOpen : t.sequenceClosed}
                </div>
              )}
            </div>
          )}

          {/* Stats supplementaires */}
          {(isAdminRole || isCenseurRole) && selectedClass && selectedSequence && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-lg p-2">
                  <div className="text-xs text-blue-600 font-medium">{lang === "fr" ? "Classe" : "Class"}</div>
                  <div className="text-sm font-bold text-blue-800">
                    {classes.find(c => c.id.toString() === selectedClass)?.name || "-"}
                  </div>
                </div>
                <div className="bg-purple-50 rounded-lg p-2">
                  <div className="text-xs text-purple-600 font-medium">{lang === "fr" ? "Sequence" : "Sequence"}</div>
                  <div className="text-sm font-bold text-purple-800">
                    {sequences.find(s => s.id.toString() === selectedSequence)?.name || "-"}
                  </div>
                </div>
                <div className="bg-rose-50 rounded-lg p-2">
                  <div className="text-xs text-rose-600 font-medium">{lang === "fr" ? "Matiere" : "Subject"}</div>
                  <div className="text-sm font-bold text-rose-800">
                    {displaySubjects.find(s => (isFirstCycle ? s.name : s.id.toString()) === selectedSubject)?.name || "-"}
                  </div>
                  {selectedSubject && (
                    <div className="text-xs text-rose-600 mt-0.5">
                      coef. {displaySubjects.find(s => (isFirstCycle ? s.name : s.id.toString()) === selectedSubject)?.coefficient || 1}
                    </div>
                  )}
                </div>
                <div className="bg-amber-50 rounded-lg p-2">
                  <div className="text-xs text-amber-600 font-medium">{lang === "fr" ? "Matieres sans notes" : "Subjects without grades"}</div>
                  <div className="text-sm font-bold text-amber-800">
                    {subjectsStatus.filter(s => !s.has_grades).length} / {subjectsStatus.length}
                  </div>
                </div>
                <div className="bg-emerald-50 rounded-lg p-2">
                  <div className="text-xs text-emerald-600 font-medium">{lang === "fr" ? "Taux de remplissage" : "Fill rate"}</div>
                  <div className="text-sm font-bold text-emerald-800">
                    {subjectsStatus.length > 0 ? Math.round((subjectsStatus.filter(s => s.has_grades).length / subjectsStatus.length) * 100) : 0}%
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Stats matieres */}
          {(isAdminRole || isCenseurRole) && selectedClass && selectedSequence && subjectsStatus.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-amber-600" />
                  <span className="text-xs font-semibold text-gray-700">
                    {lang === "fr" ? "Matieres sans notes" : "Subjects without grades"}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full font-medium">
                    {subjectsStatus.filter(s => !s.has_grades).length} / {subjectsStatus.length} {lang === "fr" ? "non remplies" : "not filled"}
                  </span>
                  <span className="text-xs text-gray-500">
                    {Math.round((subjectsStatus.filter(s => s.has_grades).length / subjectsStatus.length) * 100)}% {lang === "fr" ? "complet" : "complete"}
                  </span>
                </div>
              </div>

              <div className="w-full h-2 bg-gray-100 rounded-full mb-2 overflow-hidden">
                <div 
                  className="h-full bg-emerald-500 rounded-full transition-all"
                  style={{ width: `${(subjectsStatus.filter(s => s.has_grades).length / subjectsStatus.length) * 100}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-2">
                {subjectsStatus
                  .filter(s => !s.has_grades)
                  .map(sub => (
                    <button
                      key={sub.subject_id}
                      onClick={() => {
                        setSelectedSubject(sub.subject_id.toString());
                        setGrades([]);
                        setEntryGrades({});
                      }}
                      className="px-2 py-1 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-700 hover:bg-amber-100 transition-colors flex items-center gap-1"
                      title={lang === "fr" ? `Cliquez pour saisir les notes de ${sub.subject_label}` : `Click to enter grades for ${sub.subject_label}`}
                    >
                      <span className="font-medium">{sub.subject_label}</span>
                      <span className="text-amber-500">(coef. {sub.coefficient})</span>
                    </button>
                  ))}
                {subjectsStatus.filter(s => !s.has_grades).length === 0 && (
                  <span className="text-xs text-emerald-600 font-medium">
                    ✓ {lang === "fr" ? "Toutes les matieres ont des notes" : "All subjects have grades"}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <div className="flex items-center gap-2">
              <input type="checkbox" id="showAll" checked={showAllStudents} onChange={(e) => setShowAllStudents(e.target.checked)}
                className="w-4 h-4 text-[#1e3a8a] rounded border-gray-300 focus:ring-[#1e3a8a]" />
              <label htmlFor="showAll" className="text-sm text-gray-700 cursor-pointer">{t.showAllStudents}</label>
            </div>
            <span className="text-xs text-gray-500">{filteredStudents.length} {t.studentCount}</span>
          </div>

          <div className="overflow-x-auto hidden md:block">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">{t.matricule}</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => handleSort("name")}
                  >
                    <div className="flex items-center gap-1">
                      {t.student}
                      {sortBy === "name" && (
                        <span className="text-[#1e3a8a]">{sortOrder === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t.class}</th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-28 cursor-pointer hover:bg-gray-100 transition-colors select-none"
                    onClick={() => handleSort("grade")}
                  >
                    <div className="flex items-center gap-1">
                      {t.grade}
                      {sortBy === "grade" && (
                        <span className="text-[#1e3a8a]">{sortOrder === "asc" ? "↑" : "↓"}</span>
                      )}
                    </div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-40">{t.appreciation}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{t.comment}</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider w-24">{t.actions}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" />{t.loading}</div>
                  </td></tr>
                ) : !selectedSequence ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <Award className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm">{t.noOpenSequences}</p><p className="text-xs text-gray-400 mt-1">{t.openSequenceRequired}</p>
                  </td></tr>
                ) : !selectedClass || !selectedSubject ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.selectFilters}</p>
                  </td></tr>
                ) : sortedStudents.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.noStudents}</p>
                  </td></tr>
                ) : (
                  sortedStudents.map((student) => {
                    const existingGrade = getExistingGrade(student.id);
                    const editedGrade = existingGrade ? editedGrades[existingGrade.id] : null;
                    return (
                      <tr key={student.id} className={`hover:bg-gray-50 transition-colors ${entryMode ? "bg-amber-50/30" : ""}`}>
                        <td className="px-4 py-3 text-sm font-mono text-[#1e3a8a] font-medium">{student.matricule}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#1e3a8a]/10 flex items-center justify-center text-xs font-bold text-[#1e3a8a]">
                              {(student.last_name?.[0] || "") + (student.first_name?.[0] || "")}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{displayName(student)}</p>
                              {student.gender && (<p className="text-xs text-gray-500">{student.gender === "M" ? "♂" : "♀"}</p>)}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{student.class_name || classes.find(c => c.id === student.class_id)?.name || "-"}</td>
                        <td className="px-4 py-3">
                          {entryMode ? (
                            <input type="number" min={0} max={20} step={0.25}
                              value={entryGrades[student.id]?.value !== undefined ? entryGrades[student.id]?.value ?? "" : existingGrade?.value ?? ""}
                              onChange={(e) => handleGradeChange(student.id, e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const inputs = Array.from(document.querySelectorAll('[data-grade-input]')) as HTMLInputElement[];
                                  const idx = inputs.indexOf(e.currentTarget);
                                  if (idx >= 0 && idx < inputs.length - 1) {
                                    inputs[idx + 1].focus();
                                    inputs[idx + 1].select();
                                  }
                                }
                              }}
                              data-grade-input
                              className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-center font-bold text-sm focus:ring-2 focus:ring-[#1e3a8a] focus:border-[#1e3a8a]"
                              placeholder={t.gradePlaceholder} />
                          ) : existingGrade ? (
                            editedGrade ? (
                              <input type="number" min={0} max={20} step={0.25} value={editedGrade.value}
                                onChange={(e) => handleEditGrade(existingGrade.id, parseFloat(e.target.value))}
                                className="w-24 px-2 py-1.5 border border-gray-300 rounded-lg text-center font-bold text-sm focus:ring-2 focus:ring-[#1e3a8a]" />
                            ) : (
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getAppreciationBadgeColor(existingGrade.value)}`}>
                                {existingGrade.value?.toFixed(2) ?? "-"}
                              </span>
                            )
                          ) : (<span className="text-sm text-gray-400">-</span>)}
                        </td>
                        <td className="px-4 py-3">
                          {entryMode ? (
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getAppreciationBadgeColor(entryGrades[student.id]?.value ?? null)}`}>
                              {entryGrades[student.id]?.appreciation || getAppreciation(entryGrades[student.id]?.value ?? null, lang) || "-"}
                            </span>
                          ) : existingGrade ? (
                            editedGrade ? (
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${getAppreciationBadgeColor(editedGrade.value)}`}>{editedGrade.appreciation}</span>
                            ) : (
                              <span className={`text-xs font-medium px-2 py-1 rounded-full ${getAppreciationBadgeColor(existingGrade.value)}`}>
                                {existingGrade.appreciation_fr || existingGrade.appreciation_en || getAppreciation(existingGrade.value, lang) || "-"}
                              </span>
                            )
                          ) : (<span className="text-xs text-gray-400">-</span>)}
                        </td>
                        <td className="px-4 py-3">
                          {entryMode ? (
                            <input type="text" value={entryGrades[student.id]?.comment || ""} onChange={(e) => handleCommentChange(student.id, e.target.value)}
                              placeholder={t.optionalComment} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" />
                          ) : existingGrade && editedGrade ? (
                            <input type="text" value={editedGrade.comment}
                              onChange={(e) => setEditedGrades(prev => ({ ...prev, [existingGrade.id]: { ...prev[existingGrade.id], comment: e.target.value } }))}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" />
                          ) : (
                            <span className="text-sm text-gray-600 truncate max-w-[200px] block">{existingGrade?.comment || "-"}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {existingGrade && !entryMode && (
                            <div className="flex items-center justify-end gap-1">
                              {editedGrade ? (
                                <>
                                  <button onClick={() => handleUpdateGrade(existingGrade.id)} className="p-1.5 hover:bg-green-50 rounded-lg text-green-600 transition-colors" title={t.save}><Save className="w-4 h-4" /></button>
                                  <button onClick={() => setEditedGrades(prev => { const next = { ...prev }; delete next[existingGrade.id]; return next; })}
                                    className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors" title={t.cancel}><X className="w-4 h-4" /></button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => handleEditGrade(existingGrade.id, existingGrade.value || 0)}
                                    className="p-1.5 hover:bg-blue-50 rounded-lg text-[#1e3a8a] transition-colors" title={t.edit}><Edit2 className="w-4 h-4" /></button>
                                  <button onClick={() => setShowDeleteConfirm(existingGrade.id)}
                                    className="p-1.5 hover:bg-red-50 rounded-lg text-red-500 transition-colors" title={t.delete}><Trash2 className="w-4 h-4" /></button>
                                </>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* ─── Vue mobile : cartes élèves — la note est directement visible
              (le tableau 7 colonnes force un scroll horizontal illisible sur téléphone) ─── */}
          <div className="md:hidden divide-y divide-gray-100">
            {loading ? (
              <div className="py-12 text-center text-gray-500 flex items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />{t.loading}
              </div>
            ) : sortedStudents.length === 0 ? (
              <div className="py-12 text-center text-gray-500">
                {!selectedSequence ? t.noOpenSequences : (!selectedClass || !selectedSubject) ? t.selectFilters : t.noStudents}
              </div>
            ) : (
              sortedStudents.map(renderMobileStudentCard)
            )}
          </div>

          {entryMode && sortedStudents.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
              <span className="text-sm text-gray-600">{Object.values(entryGrades).filter(e => e.value !== null && !isNaN(e.value)).length} {t.modifiedGrades}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setEntryGrades({})} className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">{t.cancel}</button>
                <button onClick={handleBulkSave} disabled={saving}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  {saving ? t.processing : t.save}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Sequence Modal */}
      {showSequenceModal && (
        <SequenceModal 
          sequences={sequences} 
          evaluationPeriods={evaluationPeriods} 
          onClose={() => setShowSequenceModal(false)}
          onToggle={handleToggleSequence} 
          onSuccess={loadInitialData} 
          t={t} 
          lang={lang} 
          schoolId={schoolId} 
          selectedAcademicYearId={selectedAcademicYearId}
          canEdit={!!(auth.isAdmin || auth.isSuperAdmin)}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Upload className="w-5 h-5 text-[#1e3a8a]" />{t.import}</h2>
              <button onClick={() => { setShowImportModal(false); setImportFile(null); }} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div onDragOver={(e) => e.preventDefault()} onDrop={handleFileDrop}
                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-[#1e3a8a] hover:bg-blue-50/50 transition-colors cursor-pointer"
                onClick={() => document.getElementById("fileInput")?.click()}>
                <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm text-gray-600 font-medium">{t.dragDrop}</p>
                <p className="text-xs text-gray-400 mt-1">{t.orClick}</p>
                <input id="fileInput" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => e.target.files?.[0] && setImportFile(e.target.files[0])} className="hidden" />
              </div>

              {importFile && (
                <div className="bg-blue-50 rounded-lg p-3 flex items-center gap-3">
                  <FileSpreadsheet className="w-5 h-5 text-blue-600" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{importFile.name}</p>
                    <p className="text-xs text-gray-500">{(importFile.size / 1024).toFixed(1)} KB</p>
                  </div>
                  <button onClick={() => setImportFile(null)} className="p-1 hover:bg-blue-100 rounded-lg text-blue-600"><X className="w-4 h-4" /></button>
                </div>
              )}

              <div className="flex items-center gap-2">
                <button onClick={handleImportExcel} disabled={!importFile || importLoading}
                  className="flex-1 px-4 py-2.5 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center justify-center gap-2 transition-colors">
                  {importLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {importLoading ? t.processing : t.import}
                </button>
                <button onClick={() => { setShowImportModal(false); setImportFile(null); }}
                  className="px-4 py-2.5 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t.cancel}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <h3 className="text-lg font-semibold text-gray-900">{lang === "fr" ? "Confirmation" : "Confirm"}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{t.confirmDelete}</p>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setShowDeleteConfirm(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t.cancel}</button>
              <button onClick={() => handleDeleteGrade(showDeleteConfirm)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">{t.delete}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ==================== SEQUENCE MODAL ====================
function SequenceModal({
  sequences: initialSequences,
  evaluationPeriods: initialPeriods,
  onClose,
  onToggle,
  onSuccess,
  t,
  lang,
  schoolId,
  selectedAcademicYearId,
  canEdit,
}: {
  sequences: GradeSequence[];
  evaluationPeriods: EvaluationPeriod[];
  onClose: () => void;
  onToggle: (id: number) => void;
  onSuccess: () => void;
  t: any;
  lang: 'fr' | 'en';
  schoolId: number | null;
  selectedAcademicYearId: number | null;
  /** Édition réservée au super admin / admin */
  canEdit?: boolean;
}) {
  const [activeTab, setActiveTab] = useState<'sequences' | 'periods'>('sequences');

  const [modalSequences, setModalSequences] = useState<GradeSequence[]>(initialSequences);
  const [modalPeriods, setModalPeriods] = useState<EvaluationPeriod[]>(initialPeriods);
  const [modalLoading, setModalLoading] = useState(false);

  const [seqForm, setSeqForm] = useState({ evaluation_period_id: "", label: "", code: "", start_date: "", end_date: "" });
  const [seqSaving, setSeqSaving] = useState(false);
  const [periodForm, setPeriodForm] = useState({ code: "T1", label_fr: "", label_en: "", start_date: "", end_date: "", order_index: "1" });
  const [periodSaving, setPeriodSaving] = useState(false);
  const [deletePeriodId, setDeletePeriodId] = useState<number | null>(null);
  const [deleteSequenceId, setDeleteSequenceId] = useState<number | null>(null);
  // Édition (super admin / admin uniquement)
  const [editingSequence, setEditingSequence] = useState<GradeSequence | null>(null);
  const [editingPeriod, setEditingPeriod] = useState<EvaluationPeriod | null>(null);

  const loadModalData = useCallback(async () => {
    if (!selectedAcademicYearId) {
      toast.error(lang === 'fr' ? 'Aucune année scolaire sélectionnée' : 'No academic year selected');
      return;
    }

    setModalLoading(true);
    try {
      const params: Record<string, any> = {};
      if (schoolId) params.school_id = schoolId;
      if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;

      let periodsData: any[] = [];
      try {
        const res = await apiService.get<any>("/grades/periods", { params });
        periodsData = Array.isArray(res) ? res : extractData(res);
      } catch (err) {
        console.warn('[SequenceModal] periods fetch failed', err);
        periodsData = [];
      }

      const periods: EvaluationPeriod[] = Array.isArray(periodsData) ? periodsData.map((p: any) => ({
        id: p.id,
        code: p.code || "T1",
        label_fr: p.label_fr || `Trimestre ${p.id}`,
        label_en: p.label_en || `Term ${p.id}`,
        start_date: p.start_date || "",
        end_date: p.end_date || "",
        is_active: p.is_active !== false,
        sequences: []
      })) : [];

      let seqsData: any[] = [];
      try {
        const res = await apiService.get<any>("/grades/sequences", { params });
        seqsData = Array.isArray(res) ? res : extractData(res);

        if (Array.isArray(seqsData) && selectedAcademicYearId) {
          seqsData = seqsData.filter((s: any) => {
            const matchesYear = (s.academic_year_id === undefined || s.academic_year_id === null) ? true : Number(s.academic_year_id) === Number(selectedAcademicYearId);
            const matchesSchool = (s.school_id === undefined || s.school_id === null || !schoolId) ? true : Number(s.school_id) === Number(schoolId);
            return matchesYear && matchesSchool;
          });
        }
      } catch (err) {
        console.warn('[SequenceModal] sequences fetch failed', err);
        seqsData = [];
      }

      const seqs: GradeSequence[] = Array.isArray(seqsData) ? seqsData.map((s: any) => ({
        id: s.id,
        name: s.label || s.name || `Sequence ${s.id}`,
        label: s.label,
        code: s.code,
        start_date: s.start_date,
        end_date: s.end_date,
        is_open: s.is_active !== false,
        academic_year_id: s.academic_year_id || selectedAcademicYearId,
        evaluation_period_id: s.evaluation_period_id,
        max_score: s.max_score || 20
      })) : [];

      periods.forEach(p => {
        p.sequences = seqs.filter(s => s.evaluation_period_id === p.id);
      });

      setModalPeriods(periods);
      setModalSequences(seqs);

      console.debug('[SequenceModal] Data loaded for year', selectedAcademicYearId, {
        periods: periods.length,
        sequences: seqs.length
      });
    } catch (err) {
      console.error("[SequenceModal] Error loading data:", err);
      toast.error(lang === 'fr' ? 'Erreur de chargement des données' : 'Error loading data');
    } finally {
      setModalLoading(false);
    }
  }, [schoolId, selectedAcademicYearId, lang]);

  useEffect(() => {
    loadModalData();
  }, [loadModalData]);

  const startEditSequence = (seq: GradeSequence) => {
    setEditingSequence(seq);
    setSeqForm({
      evaluation_period_id: seq.evaluation_period_id ? String(seq.evaluation_period_id) : "",
      label: seq.name || '',
      code: seq.code || '',
      start_date: seq.start_date ? String(seq.start_date).slice(0, 10) : '',
      end_date: seq.end_date ? String(seq.end_date).slice(0, 10) : '',
    });
  };

  const cancelEditSequence = () => {
    setEditingSequence(null);
    setSeqForm({ evaluation_period_id: "", label: "", code: "", start_date: "", end_date: "" });
  };

  const handleCreateSequence = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seqForm.evaluation_period_id) { toast.error(lang === 'fr' ? 'Selectionnez une periode' : 'Select a period'); return; }
    if (!selectedAcademicYearId) { toast.error(lang === 'fr' ? 'Aucune annee scolaire selectionnee' : 'No academic year selected'); return; }
    setSeqSaving(true);
    try {
      if (editingSequence) {
        // ═══ ÉDITION (super admin / admin) ═══
        await apiService.put(`/grades/sequences/${editingSequence.id}`, {
          evaluation_period_id: parseInt(seqForm.evaluation_period_id),
          code: seqForm.code || undefined,
          label: seqForm.label,
          start_date: seqForm.start_date || undefined,
          end_date: seqForm.end_date || undefined,
        });
        toast.success(lang === 'fr' ? 'Sequence mise a jour' : 'Sequence updated');
        setEditingSequence(null);
      } else {
        const sequenceData: any = {
          evaluation_period_id: parseInt(seqForm.evaluation_period_id),
          code: seqForm.code || seqForm.label?.toUpperCase().replace(/\s+/g, '_').substring(0, 20) || 'SEQ',
          label: seqForm.label,
          start_date: seqForm.start_date,
          end_date: seqForm.end_date,
          max_score: 20,
          weight: 1.0,
          is_active: true,
          order_index: 1,
          academic_year_id: selectedAcademicYearId,
        };
        if (schoolId) sequenceData.school_id = schoolId;
        await apiService.post('/grades/sequences', sequenceData);
        toast.success(lang === 'fr' ? 'Sequence creee' : 'Sequence created');
      }
      onSuccess();
      loadModalData();
      setSeqForm({ evaluation_period_id: "", label: "", code: "", start_date: "", end_date: "" });
    } catch (err: any) { toast.error(getErrorDetail(err, t.error)); } finally { setSeqSaving(false); }
  };

  const startEditPeriod = (p: EvaluationPeriod) => {
    setEditingPeriod(p);
    setPeriodForm({
      code: p.code || 'T1',
      label_fr: p.label_fr || '',
      label_en: p.label_en || '',
      start_date: p.start_date ? String(p.start_date).slice(0, 10) : '',
      end_date: p.end_date ? String(p.end_date).slice(0, 10) : '',
      order_index: String((p as any).order_index ?? (p as any).id ?? 1),
    });
  };

  const cancelEditPeriod = () => {
    setEditingPeriod(null);
    setPeriodForm({ code: "T1", label_fr: "", label_en: "", start_date: "", end_date: "", order_index: "1" });
  };

  const handleCreatePeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAcademicYearId) { toast.error(lang === 'fr' ? 'Aucune annee scolaire selectionnee' : 'No academic year selected'); return; }
    setPeriodSaving(true);
    try {
      if (editingPeriod) {
        // ═══ ÉDITION (super admin / admin) ═══
        await apiService.put(`/grades/periods/${editingPeriod.id}`, {
          code: periodForm.code,
          label_fr: periodForm.label_fr,
          label_en: periodForm.label_en || periodForm.label_fr,
          start_date: periodForm.start_date || undefined,
          end_date: periodForm.end_date || undefined,
          order_index: parseInt(periodForm.order_index) || 1,
        });
        toast.success(lang === 'fr' ? 'Periode mise a jour' : 'Period updated');
        setEditingPeriod(null);
      } else {
        const periodData: any = {
          academic_year_id: selectedAcademicYearId,
          code: periodForm.code,
          label_fr: periodForm.label_fr,
          label_en: periodForm.label_en || periodForm.label_fr,
          start_date: periodForm.start_date,
          end_date: periodForm.end_date,
          is_active: true,
          order_index: parseInt(periodForm.order_index) || 1,
        };
        if (schoolId) periodData.school_id = schoolId;
        await apiService.post('/grades/periods', periodData);
        toast.success(lang === 'fr' ? 'Periode creee' : 'Period created');
      }
      onSuccess();
      loadModalData();
      setPeriodForm({ code: "T1", label_fr: "", label_en: "", start_date: "", end_date: "", order_index: "1" });
    } catch (err: any) { toast.error(getErrorDetail(err, t.error)); } finally { setPeriodSaving(false); }
  };

  const handleDeletePeriod = async (periodId: number) => {
    try {
      await apiService.delete(`/grades/periods/${periodId}`);
      toast.success(lang === 'fr' ? 'Periode supprimee' : 'Period deleted');
      setDeletePeriodId(null);
      onSuccess();
      loadModalData();
    } catch (err: any) { toast.error(getErrorDetail(err, t.error)); }
  };

  const handleDeleteSequence = async (sequenceId: number) => {
    try {
      await apiService.delete(`/grades/sequences/${sequenceId}`);
      toast.success(lang === 'fr' ? 'Sequence supprimee' : 'Sequence deleted');
      setDeleteSequenceId(null);
      onSuccess();
      loadModalData();
    } catch (err: any) { toast.error(getErrorDetail(err, t.error)); }
  };

  const displayPeriods = modalPeriods;
  const displaySequences = modalSequences;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#1e3a8a]" />
              {t.sequencesTitle}
            </h2>
            {selectedAcademicYearId && (
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
                ID: {selectedAcademicYearId}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={loadModalData} 
              disabled={modalLoading}
              className="p-2 hover:bg-gray-100 rounded-lg text-gray-500 transition-colors"
              title={lang === 'fr' ? 'Actualiser' : 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 ${modalLoading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="px-6 pt-4 flex gap-2 border-b border-gray-100">
          <button onClick={() => setActiveTab('sequences')} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'sequences' ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t.manageSequences}</button>
          <button onClick={() => setActiveTab('periods')} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${activeTab === 'periods' ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{t.managePeriods}</button>
        </div>

        <div className="p-6 space-y-6">
          {modalLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-[#1e3a8a]" />
              <span className="ml-2 text-sm text-gray-600">
                {lang === 'fr' ? 'Chargement...' : 'Loading...'}
              </span>
            </div>
          ) : activeTab === 'sequences' ? (
            <>
              <form onSubmit={handleCreateSequence} className="space-y-4 border-b border-gray-200 pb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {editingSequence
                      ? (lang === 'fr' ? `Modifier la séquence : ${editingSequence.name}` : `Edit sequence: ${editingSequence.name}`)
                      : t.newSequence}
                  </h3>
                  {editingSequence && (
                    <button type="button" onClick={cancelEditSequence}
                      className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-all">
                      {lang === 'fr' ? 'Annuler la modification' : 'Cancel edit'}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.period}</label>
                    <select value={seqForm.evaluation_period_id} onChange={(e) => setSeqForm({ ...seqForm, evaluation_period_id: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required>
                      <option value="">{t.selectPeriod}</option>
                      {displayPeriods.map((p) => (<option key={p.id} value={p.id}>{lang === 'fr' ? p.label_fr : p.label_en}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.sequenceName}</label>
                    <input placeholder={t.sequenceName} value={seqForm.label} onChange={(e) => setSeqForm({ ...seqForm, label: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.code}</label>
                    <input placeholder="SEQ1" value={seqForm.code} onChange={(e) => setSeqForm({ ...seqForm, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" maxLength={20} />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.startDate}</label>
                    <input type="date" value={seqForm.start_date} onChange={(e) => setSeqForm({ ...seqForm, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.endDate}</label>
                    <input type="date" value={seqForm.end_date} onChange={(e) => setSeqForm({ ...seqForm, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                </div>
                <button type="submit" disabled={seqSaving} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm disabled:opacity-50 hover:bg-[#152a5e] transition-colors">
                  {seqSaving ? t.processing : editingSequence ? (lang === 'fr' ? 'Enregistrer les modifications' : 'Save changes') : t.create}
                </button>
              </form>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{t.existingSequences}</h3>
                {displayPeriods.length === 0 ? (
                  <div className="text-center py-8">
                    <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">{lang === 'fr' ? 'Aucune période créée' : 'No periods created'}</p>
                    <p className="text-xs text-gray-400 mt-1">{lang === 'fr' ? "Créez d'abord une période" : 'Create a period first'}</p>
                  </div>
                ) : (
                  displayPeriods.map(period => {
                    const periodSequences = displaySequences.filter(s => s.evaluation_period_id === period.id);
                    return (
                      <div key={period.id} className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{lang === 'fr' ? period.label_fr : period.label_en}</h4>
                          <span className="text-xs text-gray-400">{periodSequences.length} sequence(s)</span>
                        </div>
                        {periodSequences.length === 0 ? (
                          <p className="text-xs text-gray-400 italic pl-2">{lang === 'fr' ? 'Aucune séquence' : 'No sequences'}</p>
                        ) : (
                          <div className="overflow-x-auto border rounded-lg">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.sequenceName}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.code}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.startDate}</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.endDate}</th>
                                  <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">{t.status}</th>
                                  <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">{t.actions}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {periodSequences.map((seq) => (
                                  <tr key={seq.id} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 font-medium">{seq.name}</td>
                                    <td className="px-3 py-2 text-gray-600 font-mono text-xs">{seq.code || '-'}</td>
                                    <td className="px-3 py-2 text-gray-600">{seq.start_date || '-'}</td>
                                    <td className="px-3 py-2 text-gray-600">{seq.end_date || '-'}</td>
                                    <td className="px-3 py-2 text-center">
                                      <button onClick={() => onToggle(seq.id)}
                                        className={`px-2 py-1 rounded-full text-xs font-medium transition-colors ${seq.is_open ? "bg-green-100 text-green-700 hover:bg-green-200" : "bg-red-100 text-red-700 hover:bg-red-200"}`}>
                                        {seq.is_open ? t.sequenceOpen : t.sequenceClosed}
                                      </button>
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <div className="flex items-center justify-end gap-1">
                                        <button onClick={() => onToggle(seq.id)} className="p-1 hover:bg-blue-50 rounded text-blue-600" title={seq.is_open ? t.close : t.open}>
                                          {seq.is_open ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
                                        </button>
                                        {canEdit && (
                                          <button onClick={() => startEditSequence(seq)} className="p-1 hover:bg-amber-50 rounded text-amber-600" title={lang === 'fr' ? 'Modifier' : 'Edit'}><Pencil className="w-3.5 h-3.5" /></button>
                                        )}
                                        <button onClick={() => setDeleteSequenceId(seq.id)} className="p-1 hover:bg-red-50 rounded text-red-600" title={t.delete}><Trash2 className="w-3.5 h-3.5" /></button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
                {displaySequences.length === 0 && displayPeriods.length > 0 && (
                  <div className="text-center py-8">
                    <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">{t.noSequences}</p>
                    <p className="text-xs text-gray-400 mt-1">{t.createFirstSequence}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <form onSubmit={handleCreatePeriod} className="space-y-4 border-b border-gray-200 pb-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {editingPeriod
                      ? (lang === 'fr' ? `Modifier la période : ${editingPeriod.label_fr}` : `Edit period: ${editingPeriod.label_fr}`)
                      : t.newPeriod}
                  </h3>
                  {editingPeriod && (
                    <button type="button" onClick={cancelEditPeriod}
                      className="text-xs px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-all">
                      {lang === 'fr' ? 'Annuler la modification' : 'Cancel edit'}
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.code}</label>
                    <select value={periodForm.code} onChange={(e) => setPeriodForm({ ...periodForm, code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required>
                      <option value="T1">T1 - {lang === 'fr' ? 'Trimestre 1' : 'Term 1'}</option>
                      <option value="T2">T2 - {lang === 'fr' ? 'Trimestre 2' : 'Term 2'}</option>
                      <option value="T3">T3 - {lang === 'fr' ? 'Trimestre 3' : 'Term 3'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.order}</label>
                    <input type="number" min={1} value={periodForm.order_index} onChange={(e) => setPeriodForm({ ...periodForm, order_index: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.periodName} (FR)</label>
                    <input placeholder={lang === 'fr' ? 'Trimestre 1' : 'Term 1'} value={periodForm.label_fr} onChange={(e) => setPeriodForm({ ...periodForm, label_fr: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.periodName} (EN)</label>
                    <input placeholder={lang === 'fr' ? 'Term 1' : 'Term 1'} value={periodForm.label_en} onChange={(e) => setPeriodForm({ ...periodForm, label_en: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.startDate}</label>
                    <input type="date" value={periodForm.start_date} onChange={(e) => setPeriodForm({ ...periodForm, start_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t.endDate}</label>
                    <input type="date" value={periodForm.end_date} onChange={(e) => setPeriodForm({ ...periodForm, end_date: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]" required />
                  </div>
                </div>
                <button type="submit" disabled={periodSaving} className="px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm disabled:opacity-50 hover:bg-[#152a5e] transition-colors">
                  {periodSaving ? t.processing : editingPeriod ? (lang === 'fr' ? 'Enregistrer les modifications' : 'Save changes') : t.create}
                </button>
              </form>

              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-gray-900">{lang === 'fr' ? 'Périodes existantes' : 'Existing periods'}</h3>
                {displayPeriods.length === 0 ? (
                  <div className="text-center py-8">
                    <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">{lang === 'fr' ? 'Aucune période créée' : 'No periods created'}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.code}</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.periodName}</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.startDate}</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-600">{t.endDate}</th>
                          <th className="px-3 py-2 text-center text-xs font-medium text-gray-600">{t.order}</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-600">{t.actions}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {displayPeriods.map((period) => (
                          <tr key={period.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-mono text-xs text-gray-600">{period.code}</td>
                            <td className="px-3 py-2 font-medium">{lang === 'fr' ? period.label_fr : period.label_en}</td>
                            <td className="px-3 py-2 text-gray-600">{period.start_date || '-'}</td>
                            <td className="px-3 py-2 text-gray-600">{period.end_date || '-'}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{period.id}</td>
                            <td className="px-3 py-2 text-right">
                              {canEdit && (
                                <button onClick={() => startEditPeriod(period)} className="p-1 hover:bg-amber-50 rounded text-amber-600" title={lang === 'fr' ? 'Modifier' : 'Edit'}><Pencil className="w-3.5 h-3.5" /></button>
                              )}
                              <button onClick={() => setDeletePeriodId(period.id)} className="p-1 hover:bg-red-50 rounded text-red-600" title={t.delete}><Trash2 className="w-3.5 h-3.5" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Period Confirmation */}
      {deletePeriodId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <h3 className="text-lg font-semibold text-gray-900">{lang === 'fr' ? 'Confirmation' : 'Confirm'}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{t.confirmDeletePeriod}</p>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setDeletePeriodId(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t.cancel}</button>
              <button onClick={() => handleDeletePeriod(deletePeriodId)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">{t.delete}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Sequence Confirmation */}
      {deleteSequenceId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-600" /></div>
              <h3 className="text-lg font-semibold text-gray-900">{lang === 'fr' ? 'Confirmation' : 'Confirm'}</h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">{t.confirmDeleteSequence}</p>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setDeleteSequenceId(null)} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors">{t.cancel}</button>
              <button onClick={() => handleDeleteSequence(deleteSequenceId)} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors">{t.delete}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}